import { Howl } from 'howler';
import { getFlips, tmx } from 'tmx-map-parser';

/**
 * A class representing a color.
 */
class Color {
    /**
     * Creates a new instance of the Color class.
     * @param rOrHex - The red channel value or a hexadecimal color string.
     * @param g - The green channel value.
     * @param b - The blue channel value.
     * @param a - The alpha channel value.
     */
    constructor(rOrHex = 255, g = 255, b = 255, a = 1) {
        /** The red channel value. */
        this.r = 255;
        /** The green channel value. */
        this.g = 255;
        /** The blue channel value. */
        this.b = 255;
        /** The alpha channel value. */
        this.a = 1;
        if (typeof rOrHex === 'string') {
            this.setHex(rOrHex);
        }
        else {
            this.r = rOrHex;
            this.g = g;
            this.b = b;
            this.a = a;
        }
    }
    /**
     * Creates a new instance of the Color object with the same property values.
     * @returns A new instance of the Color object.
     */
    copy() {
        return new Color(this.r, this.g, this.b, this.a);
    }
    /**
     * Adds the values of another Color object to this Color object and returns a new Color object.
     * @param c - The Color object to be added.
     * @returns A new Color object with the summed values.
     */
    add(c) {
        return new Color(this.r + c.r, this.g + c.g, this.b + c.b, this.a + c.a);
    }
    /**
     * Subtracts the given color from the current color.
     * @param c - The color to subtract.
     * @returns A new `Color` object representing the result of the subtraction.
     */
    subtract(c) {
        return new Color(this.r - c.r, this.g - c.g, this.b - c.b, this.a - c.a);
    }
    /**
     * Sets the alpha value of the color.
     * @param a - The alpha value to set.
     * @returns The updated Color instance.
     */
    setAlpha(a) {
        this.a = a;
        return this;
    }
    /**
     * Sets the color values based on the given hexadecimal color code.
     * @param hex - The hexadecimal color code (e.g., "#FF0000").
     * @returns The Color instance with updated color values.
     */
    setHex(hex) {
        const tempHex = hex.replace('#', '');
        this.r = parseInt(tempHex.substring(0, 2), 16);
        this.g = parseInt(tempHex.substring(2, 4), 16);
        this.b = parseInt(tempHex.substring(4, 6), 16);
        this.a = tempHex.length === 8 ? parseInt(tempHex.substring(6, 8), 16) / 255 : 1;
        return this;
    }
    /**
     * Adjusts the brightness of the color by the specified amount.
     * @param amount - The amount by which to adjust the brightness. Positive values make the color brighter,
     *                 while negative values make it darker.
     * @returns A new `Color` object with the adjusted brightness.
     */
    brightness(amount) {
        let { r, g, b } = this;
        r = r + amount;
        g = g + amount;
        b = b + amount;
        if (r > 255)
            r = 255;
        else if (r < 0)
            r = 0;
        if (g > 255)
            g = 255;
        else if (g < 0)
            g = 0;
        if (b > 255)
            b = 255;
        else if (b < 0)
            b = 0;
        return new Color(r, g, b, this.a);
    }
    /**
     * Returns the color as a hexadecimal string representation.
     * @param includeAlpha - Whether to include the alpha channel in the string representation. Default is true.
     * @returns The color as a hexadecimal string.
     */
    toString(includeAlpha = true) {
        const toHex = (c) => ((c = c | 0) < 16 ? '0' : '') + c.toString(16);
        return '#' + toHex(this.r) + toHex(this.g) + toHex(this.b) + (includeAlpha ? toHex(this.a * 255) : '');
    }
}

/**
 * DawnBringer 16 color palette
 * @see {@link https://lospec.com/palette-list/dawnbringer-16}
 * @see {@link https://pixeljoint.com/forum/forum_posts.asp?TID=12795}
 */
const DefaultColors = {
    Black: new Color('#140C1C'),
    DarkRed: new Color('#442434'),
    DarkBlue: new Color('#30346D'),
    DarkGray: new Color('#4E4A4F'),
    Brown: new Color('#854C30'),
    DarkGreen: new Color('#346524'),
    Red: new Color('#D04648'),
    LightGray: new Color('#757161'),
    LightBlue: new Color('#597DCE'),
    Orange: new Color('#D27D2C'),
    BlueGray: new Color('#8595A1'),
    LightGreen: new Color('#6DAA2C'),
    Peach: new Color('#D2AA99'),
    Cyan: new Color('#6DC2CA'),
    Yellow: new Color('#DAD45E'),
    White: new Color('#DEEED6')
};
/**
 * The type of a node in a Tiled map.
 */
var NodeType;
(function (NodeType) {
    NodeType["Custom"] = "custom";
    NodeType["ImageLayer"] = "imagelayer";
    NodeType["Layer"] = "layer";
    NodeType["ObjectGroup"] = "objectgroup";
    NodeType["Properties"] = "properties";
    NodeType["TileSet"] = "tileset";
})(NodeType || (NodeType = {}));
/**
 * The flipped flags for a tile in a Tiled map.
 */
var Flipped;
(function (Flipped) {
    Flipped[Flipped["Diagonally"] = 536870912] = "Diagonally";
    Flipped[Flipped["Horizontally"] = 2147483648] = "Horizontally";
    Flipped[Flipped["Vertically"] = 1073741824] = "Vertically";
})(Flipped || (Flipped = {}));
/**
 * Represents the shape types.
 */
var Shape;
(function (Shape) {
    Shape["Rectangle"] = "rectangle";
    Shape["Ellipse"] = "ellipse";
    Shape["Polygon"] = "polygon";
})(Shape || (Shape = {}));
const BodyStyle = `
    margin: 0;
    overflow: hidden;
    touch-action: none; 
    user-select: none; 
    -webkit-user-select: none;
    -webkit-touch-callout: none;
`;
const CanvasStyle = `
    position: absolute;
    top: 50%;
    left: 50%;
    transform: translate(-50%,-50%);
`;
const PixelFontImage = `data:image/png;base64,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
`;

/**
 * Returns the current high-resolution timestamp, if available.
 * If the `performance` object is not available, it returns 0.
 *
 * @returns The current high-resolution timestamp, or 0 if not available.
 */
const getPerformance = () => (typeof performance !== 'undefined' && performance.now()) || 0;
/**
 * Checks if the given value is a valid array.
 *
 * @param arr - The value to be checked.
 * @returns A boolean indicating whether the value is a valid array.
 */
const isValidArray = (arr) => !!(arr && arr.length);
/**
 * Returns the filename from the given path.
 *
 * @param path - The path from which to extract the filename.
 * @returns The filename extracted from the path.
 */
const getFilename = (path) => path.replace(/^.*[\\\/]/, '');
/**
 * Converts an angle from radians to degrees.
 *
 * @param rad - The angle in radians.
 * @returns The angle converted to degrees.
 */
const rad2deg = (rad) => (rad * 180) / Math.PI;
/**
 * Converts an angle from degrees to radians.
 *
 * @param deg - The angle in degrees.
 * @returns The angle converted to radians.
 */
const deg2rad = (deg) => (deg * Math.PI) / 180;
/**
 * Clamps a value between a minimum and maximum value.
 *
 * @param val - The value to be clamped.
 * @param min - The minimum value.
 * @param max - The maximum value.
 * @returns The clamped value.
 */
const clamp = (val, min = 0, max = 1) => Math.max(min, Math.min(max, val));
/**
 * Performs linear interpolation between two values.
 *
 * @param percent - The interpolation factor.
 * @param a - The start value.
 * @param b - The end value.
 * @returns The interpolated value.
 */
const lerp = (percent, a, b) => a + clamp(percent) * (b - a);
/**
 * Generates a random number between two values.
 *
 * @param a - The lower bound.
 * @param b - The upper bound.
 * @returns The random number generated.
 */
const rand = (a = 1, b = 0) => b + Math.random() * (a - b);
/**
 * Calculates the percentage of a value between two other values.
 *
 * @param val - The value to calculate the percentage for.
 * @param a - The lower bound.
 * @param b - The upper bound.
 * @returns The percentage of the value between the bounds.
 */
const percent = (val, a, b) => (b - a ? clamp((val - a) / (b - a)) : 0);
/**
 * Generates a random integer between two values.
 *
 * @param valueA - The lower bound.
 * @param valueB - The upper bound.
 * @returns The random integer generated.
 */
const randInt = (valueA, valueB = 0) => Math.floor(rand(valueA, valueB));
/**
 * Sorts objects based on their render order.
 *
 * @param a - The first object to compare.
 * @param b - The second object to compare.
 * @returns A negative value if `a` should be sorted before `b`, a positive value if `a` should be sorted after `b`, or 0 if they have the same render order.
 */
const sortByRenderOrder = (a, b) => a.renderOrder - b.renderOrder;
/**
 * Delays the execution for the specified amount of time.
 *
 * @param time - The time to delay in milliseconds.
 * @returns A promise that resolves after the specified delay.
 */
const delay = (time) => new Promise(resolve => setTimeout(resolve, time));
const wait = (time, cb) => delay(time).then(cb);
/**
 * Normalizes a number within a specified range.
 *
 * @param n - The number to normalize.
 * @param min - The minimum value of the range.
 * @param max - The maximum value of the range.
 * @returns The normalized number within the specified range.
 */
function normalize(n, min, max) {
    const range = max - min;
    return ((((n - min) % range) + range) % range) + min;
}

/**
 * Represents a 2D vector with x and y components.
 */
class Vector {
    /**
     * Creates a new vector.
     * @param x - The x-coordinate of the vector.
     * @param y - The y-coordinate of the vector.
     */
    constructor(x = 0, y = 0) {
        this.x = x;
        this.y = y;
    }
    /**
     * Adds the given vector to this vector and returns a new vector.
     * @param v - The vector to add.
     * @returns A new vector representing the sum of this vector and the given vector.
     */
    add(v) {
        return new Vector(this.x + v.x, this.y + v.y);
    }
    /**
     * Subtracts the given vector from this vector and returns a new vector.
     * @param v - The vector to subtract.
     * @returns A new vector representing the result of the subtraction.
     */
    subtract(v) {
        return new Vector(this.x - v.x, this.y - v.y);
    }
    /**
     * Multiplies the current vector by another vector.
     * @param v The vector to multiply by.
     * @returns A new vector that is the result of the multiplication.
     */
    multiply(v) {
        return new Vector(this.x * v.x, this.y * v.y);
    }
    /**
     * Divides the vector by either another vector or a number.
     * If the argument is a number, it divides each component of the vector by that number.
     * If the argument is a vector, it divides the corresponding components of the vector
     * by the components of the argument vector.
     * @param d - The vector or number to divide by.
     * @returns A new vector resulting from the division.
     */
    divide(d) {
        if (typeof d === 'number')
            return new Vector(this.x / d, this.y / d);
        return new Vector(this.x / d.x, this.y / d.y);
    }
    /**
     * Creates a new Vector object with the same x and y values as the current vector.
     * @returns A new Vector object with the same x and y values.
     */
    clone() {
        return new Vector(this.x, this.y);
    }
    /**
     * Scales the vector by a given factor.
     * @param s - The scaling factor.
     * @returns A new Vector instance representing the scaled vector.
     */
    scale(s) {
        return new Vector(this.x * s, this.y * s);
    }
    /**
     * Calculates the length of the vector.
     * @returns The length of the vector.
     */
    length() {
        return Math.pow(this.lengthSquared(), 0.5);
    }
    /**
     * Calculates the squared length of the vector.
     * @returns The squared length of the vector.
     */
    lengthSquared() {
        return Math.pow(this.x, 2) + Math.pow(this.y, 2);
    }
    /**
     * Calculates the Euclidean distance between this vector and the given vector.
     * @param v - The vector to calculate the distance to.
     * @returns The Euclidean distance between the two vectors.
     */
    distance(v) {
        return Math.pow(this.distanceSquared(v), 0.5);
    }
    /**
     * Calculates the squared distance between this vector and the given vector.
     * @param v - The vector to calculate the distance to.
     * @returns The squared distance between this vector and the given vector.
     */
    distanceSquared(v) {
        return Math.pow((this.x - v.x), 2) + Math.pow((this.y - v.y), 2);
    }
    /**
     * Normalizes the vector to a specified length.
     * If the vector's length is zero, a new vector with the specified length is returned.
     * @param length - The desired length of the normalized vector. Default is 1.
     * @returns A new Vector instance representing the normalized vector.
     */
    normalize(length = 1) {
        const l = this.length();
        return l ? this.scale(length / l) : new Vector(0, length);
    }
    /**
     * Clamps the length of the vector to a specified value.
     * If the length of the vector is greater than the specified value, the vector is scaled down.
     * If the length of the vector is less than or equal to the specified value, the vector remains unchanged.
     * @param length The maximum length to clamp the vector to. Default value is 1.
     * @returns A new vector with the clamped length.
     */
    clampLength(length = 1) {
        const l = this.length();
        return l > length ? this.scale(length / l) : this;
    }
    /**
     * Calculates the dot product between this vector and the given vector.
     * @param v - The vector to calculate the dot product with.
     * @returns The dot product of the two vectors.
     */
    dot(v) {
        return this.x * v.x + this.y * v.y;
    }
    /**
     * Calculates the cross product of this vector and the given vector.
     * @param v - The vector to calculate the cross product with.
     * @returns The cross product of the two vectors.
     */
    cross(v) {
        return this.x * v.y - this.y * v.x;
    }
    /**
     * Calculates the angle of the vector in radians.
     * The angle is measured counterclockwise from the positive x-axis.
     * @returns The angle of the vector in radians.
     */
    angle() {
        return Math.atan2(this.x, this.y);
    }
    /**
     * Sets the angle and length of the vector.
     * @param angle - The angle in radians.
     * @param length - The length of the vector.
     * @returns The modified vector.
     */
    setAngle(angle = 0, length = 1) {
        this.x = length * Math.sin(angle);
        this.y = length * Math.cos(angle);
        return this;
    }
    /**
     * Rotates the vector by the specified angle.
     * @param angle - The angle (in radians) by which to rotate the vector.
     * @returns A new Vector representing the rotated vector.
     */
    rotate(angle) {
        const c = Math.cos(angle), s = Math.sin(angle);
        return new Vector(this.x * c - this.y * s, this.x * s + this.y * c);
    }
    /**
     * Returns the direction of the vector.
     * @returns {number} The direction of the vector.
     *                  - 0: Up
     *                  - 1: Right
     *                  - 2: Down
     *                  - 3: Left
     */
    direction() {
        return Math.abs(this.x) > Math.abs(this.y) ? (this.x < 0 ? 3 : 1) : this.y < 0 ? 2 : 0;
    }
    /**
     * Returns a new Vector object with the x and y values reversed.
     * @returns A new Vector object with the x and y values reversed.
     */
    reverse() {
        return new Vector(-this.y, -this.x);
    }
    /**
     * Returns a new Vector object with the negated values of the current vector.
     * @returns {Vector} - A new Vector object with negated values.
     */
    invert() {
        return new Vector(-this.x, -this.y);
    }
    /**
     * Returns a new Vector with the components rounded down to the nearest integer.
     * @returns A new Vector with the components rounded down.
     */
    floor() {
        return new Vector(Math.floor(this.x), Math.floor(this.y));
    }
    /**
     * Calculates the area of the vector.
     * @returns The area of the vector.
     */
    area() {
        return Math.abs(this.x * this.y);
    }
    /**
     * Performs a linear interpolation between two vectors.
     * @param v - The vector to interpolate with.
     * @param percent - The interpolation factor, ranging from 0 to 1.
     * @returns The interpolated vector.
     */
    lerp(v, percent) {
        return this.add(v.subtract(this).scale(clamp(percent)));
    }
    /**
     * Checks if the current vector is within the range of the specified size.
     * @param size - The size vector to compare against.
     * @returns `true` if the current vector is within the range, `false` otherwise.
     */
    inRange(size) {
        return this.x >= 0 && this.y >= 0 && this.x < size.x && this.y < size.y;
    }
}
/**
 * Creates a 2D vector.
 * @param x - The x-coordinate of the vector.
 * @param y - The y-coordinate of the vector. If not provided, it will default to the value of `x`.
 * @returns A new Vector object representing the 2D vector.
 */
const vec2 = (x = 0, y) => new Vector(x, y === undefined ? x : y);
/**
 * Generates a random vector with an optional length.
 * @param length The length of the vector (default is 1).
 * @returns A new Vector object with a random angle and optional length.
 */
const randVector = (length = 1) => new Vector().setAngle(rand(2 * Math.PI), length);
/**
 * Generates a random point on a circle.
 * @param radius The radius of the circle (default: 1).
 * @param minRadius The minimum radius of the circle (default: 0).
 * @returns A vector representing a random point on the circle.
 */
const randPointOnCircle = (radius = 1, minRadius = 0) => radius > 0 ? randVector(radius * Math.pow(rand(minRadius / radius, 1), 0.5)) : new Vector();

/**
 * Represents a box in 2D space.
 */
class Box {
    /**
     * Creates a new instance of the Box class.
     * @param pos - The position of the box.
     * @param size - The size of the box.
     */
    constructor(pos, size) {
        this.pos = pos;
        this.size = size;
        /**
         * Checks if the box overlaps with another box.
         * @param pos - The position of the other box.
         * @param size - The size of the other box.
         * @returns True if the boxes overlap, false otherwise.
         */
        this.overlaps = (pos, size) => Math.abs(this.pos.x - pos.x) * 2 < this.size.x + size.x &&
            Math.abs(this.pos.y - pos.y) * 2 < this.size.y + size.y;
    }
    /**
     * Moves the box by a given vector.
     * @param v - The vector to move the box by.
     * @returns A new Box instance with the updated position.
     */
    move(v) {
        return new Box(this.pos.add(v), this.size);
    }
    /**
     * Scales the box by a given factor.
     * @param s - The scaling factor.
     * @returns A new Box instance with the updated size.
     */
    scale(s) {
        return new Box(this.pos.scale(s), this.size.scale(s));
    }
    /**
     * Multiplies the box by a given vector.
     * @param v - The vector to multiply the box by.
     * @returns A new Box instance with the updated position and size.
     */
    multiply(v) {
        return new Box(this.pos.multiply(v), this.size.multiply(v));
    }
    /**
     * Divides the box by a given vector.
     * @param v - The vector to divide the box by.
     * @returns A new Box instance with the updated position and size.
     */
    divide(v) {
        return new Box(this.pos.divide(v), this.size.divide(v));
    }
    /**
     * Rounds down the position and size of the box to the nearest integer.
     * @returns A new Box instance with the rounded position and size.
     */
    floor() {
        return new Box(this.pos.floor(), this.size.floor());
    }
}
/**
 * Creates a new Box instance with the specified parameters.
 * @param x - The x-coordinate of the box's position.
 * @param y - The y-coordinate of the box's position.
 * @param w - The width of the box.
 * @param h - The height of the box.
 * @returns A new Box instance.
 */
const box = (x = 0, y = 0, w = 0, h = 0) => new Box(vec2(x, y), vec2(w, h));

/**
 * The `Draw` class provides methods for drawing shapes, text, and images on a canvas.
 */
class Draw {
    /**
     * Creates a new `Draw` object.
     * @param game - The game instance.
     */
    constructor(game) {
        this.game = game;
        this.pixelFont = new Image();
        this.pixelText = () => { };
        this.pixelFont.src = PixelFontImage;
        this.pixelFont.onload = () => (this.pixelText = this.createPixelFontRenderer(this.pixelFont, 8));
    }
    /**
     * Fills a rectangle on the canvas with the specified color.
     * @param rect - The rectangle to be filled.
     * @param color - The color to fill the rectangle with.
     * @param angle - The angle (in radians) to rotate the rectangle (default: 0).
     */
    fillRect(rect, color, angle = 0) {
        const { ctx } = this.game;
        const { pos, size } = rect;
        ctx.save();
        this.rotate(rect, angle);
        ctx.fillStyle = color.toString();
        ctx.fillRect(pos.x, pos.y, size.x, size.y);
        ctx.restore();
    }
    /**
     * Draws a ellipse on the canvas with the specified color.
     * @param rect
     * @param color
     * @param angle
     */
    fillEllipse(rect, color, angle = 0) {
        const { ctx } = this.game;
        const { pos, size } = rect;
        ctx.save();
        this.rotate(rect, angle);
        ctx.beginPath();
        ctx.ellipse(pos.x + size.x / 2, pos.y + size.y / 2, size.x / 2, size.y / 2, angle, 0, Math.PI * 2);
        ctx.fillStyle = color.toString();
        ctx.fill();
        ctx.restore();
    }
    /**
     * Draws a stroke using the provided points.
     * @param x - The x-coordinate of the starting point.
     * @param y - The y-coordinate of the starting point.
     * @param points - An array of Vector objects representing the points to be connected.
     * @param lineWidth - The width of the stroke line (default: 1).
     */
    stroke(x, y, points, lineWidth = 1) {
        const { ctx } = this.game;
        ctx.lineWidth = lineWidth;
        ctx.lineJoin = 'miter';
        ctx.beginPath();
        ctx.moveTo(points[0].x + x, points[0].y + y);
        points.map((v) => ctx.lineTo(x + v.x, y + v.y));
        ctx.lineTo(points[0].x + x, points[0].y + y);
        ctx.stroke();
    }
    /**
     * Draws an outlined rectangle on the canvas.
     * @param rect - The rectangle to outline.
     * @param color - The color of the outline.
     * @param lineWidth - The width of the outline line. Default is 1.
     * @param angle - The angle of rotation for the rectangle. Default is 0.
     */
    outline(rect, color, lineWidth = 1, angle = 0) {
        const { ctx } = this.game;
        const { pos, size } = rect;
        ctx.save();
        this.rotate(rect, angle);
        ctx.strokeStyle = color.toString();
        ctx.lineWidth = lineWidth;
        ctx.beginPath();
        ctx.rect(pos.x, pos.y, size.x, size.y);
        ctx.stroke();
        ctx.restore();
    }
    /**
     * Renders text on the canvas.
     * @param text - The text to render.
     * @param pos - The position to render the text.
     * @param color - The color of the text. Defaults to white.
     * @param size - The font size of the text. Defaults to '1em'.
     * @param textAlign - The horizontal alignment of the text. Defaults to 'left'.
     * @param textBaseline - The vertical alignment of the text. Defaults to 'top'.
     * @param stroke - Whether to stroke the text. Defaults to false.
     */
    text(text, pos, color, size = '1em', textAlign = 'left', textBaseline = 'top', stroke = false) {
        const { ctx } = this.game;
        ctx.font = `${size} monospace`;
        ctx.textBaseline = textBaseline;
        ctx.textAlign = textAlign;
        if (stroke) {
            ctx.strokeStyle = DefaultColors.Black.setAlpha(0.9).toString();
            ctx.lineJoin = 'round';
            ctx.lineWidth = 4;
            ctx.strokeText(text, pos.x, pos.y);
        }
        ctx.fillStyle = (color || DefaultColors.White).toString();
        ctx.fillText(text, pos.x, pos.y);
    }
    /**
     * Draws a 2D image on the canvas.
     * @param image - The HTMLImageElement representing the image to be drawn.
     * @param rect - The bounding box of the image.
     * @param scale - The scale factor to apply to the image (default: 1).
     * @param angle - The rotation angle of the image in radians (default: 0).
     * @param flipH - Whether to flip the image horizontally (default: false).
     * @param flipV - Whether to flip the image vertically (default: false).
     * @param clip - The clipping region of the image (optional).
     * @param context - The CanvasRenderingContext2D to use for drawing (optional).
     */
    draw2d(image, rect, scale = vec2(1), angle = 0, flipH = false, flipV = false, clip, context) {
        const ctx = context || this.game.ctx;
        const { pos, size } = rect;
        const flip = flipH || flipV;
        const s = vec2(flipH ? -1 : 1, flipV ? -1 : 1);
        const f = vec2(flipH ? -size.x * scale.x : 0, flipV ? -size.y * scale.y : 0);
        const fpos = pos.subtract(f).multiply(s);
        ctx.save();
        this.rotate(rect, angle, clip ? scale : vec2(1));
        flip && ctx.scale(s.x, s.y);
        ctx.translate(0.5, 0.5);
        clip
            ? ctx.drawImage(image, clip.x, clip.y, size.x, size.y, fpos.x - 0.5, fpos.y - 0.5, size.x * scale.x, size.y * scale.y)
            : ctx.drawImage(image, pos.x - 0.5, pos.y - 0.5, size.x, size.y);
        ctx.translate(-0.5, -0.5);
        ctx.restore();
    }
    /**
     * Rotates the canvas context around a specified position and angle, and then executes a callback function.
     * @param rect - The box object containing the position and size.
     * @param angle - The angle of rotation in radians. Defaults to 0.
     * @param scale - The scale factor. Defaults to 0.
     */
    rotate(rect, angle = 0, scale = vec2(1)) {
        const { pos, size } = rect;
        const ctx = this.game.ctx;
        ctx.translate(pos.x + (size.x * scale.x) / 2, pos.y + (size.y * scale.y) / 2);
        ctx.rotate(angle);
        ctx.translate(-(pos.x + (size.x * scale.x) / 2), -(pos.y + (size.y * scale.y) / 2));
    }
    /**
     * Sets the blending mode for the canvas rendering context.
     * @param blend - A boolean indicating whether to enable blending.
     * @param context - The canvas rendering context.
     */
    setBlending(blend, context = this.game.ctx) {
        context.globalCompositeOperation = blend ? 'lighter' : 'source-over';
    }
    /**
     * Copies the content of a canvas to the main context of the game.
     * @param canvas - The canvas element to copy from.
     * @param pos - The position to place the copied content on the main context.
     * @param size - The size of the copied content on the main context.
     */
    copyToMainContext(canvas, pos, size) {
        const { ctx } = this.game;
        ctx.drawImage(canvas, pos.x, pos.y, size.x, size.y);
    }
    /**
     * Creates a pixel font renderer function.
     *
     * @param {HTMLImageElement} [image=this.pixelFont] - The image containing the pixel font.
     * @param {number} [fontSize=8] - The size of each character in the pixel font.
     * @param {string} [color='white'] - The color to apply to the pixel font.
     * @returns {Function} The pixel font renderer function.
     */
    createPixelFontRenderer(image, fontSize, color = 'white') {
        const { ctx } = this.game;
        const cols = (image.width / fontSize) | 0;
        // create a canvas for the font and apply the color
        const fontCanvas = document.createElement('canvas');
        const fontContext = fontCanvas.getContext('2d');
        fontCanvas.width = image.width;
        fontCanvas.height = image.height;
        fontContext.drawImage(image, 0, 0);
        fontContext.fillStyle = color;
        fontContext.globalCompositeOperation = 'source-in';
        fontContext.fillRect(0, 0, image.width, image.height);
        return (text, pos, scale = 1, textAlign = 'left', textBaseline = 'top') => {
            let { x, y } = pos.clone();
            if (textAlign === 'center')
                x -= (text.length * fontSize * scale) / 2;
            if (textAlign === 'right')
                x -= text.length * fontSize * scale;
            if (textBaseline === 'middle')
                y -= (fontSize * scale) / 2;
            if (textBaseline === 'bottom')
                y -= fontSize * scale;
            text.split('\n')
                .reverse()
                .forEach((output, index) => {
                for (let i = 0; i < output.length; i++) {
                    const chr = output.charCodeAt(i);
                    ctx.drawImage(fontCanvas, (chr % cols) * fontSize, Math.ceil((chr + 1) / cols - 1) * fontSize, fontSize, fontSize, Math.floor(x + i * fontSize * scale), Math.floor(y - index * (fontSize + 1)), fontSize * scale, fontSize * scale);
                }
            });
        };
    }
    /**
     * Displays a preloader with a progress bar on the canvas.
     * @param p - The progress value between 0 and 1.
     */
    preloader(p) {
        const { ctx, canvas, backgroundColor, secondaryColor } = this.game;
        const { width, height } = canvas;
        const logoSize = vec2(height / 4).floor();
        const logoPos = vec2(width / 2 - logoSize.x / 2, height / 2 - logoSize.y / 2).floor();
        const step = logoSize.x / 8;
        const gradient = ctx.createRadialGradient(width / 2, height / 2, logoSize.y, width / 2, height / 2, height);
        ctx.save();
        gradient.addColorStop(0, backgroundColor.brightness(15).toString());
        gradient.addColorStop(0.7, backgroundColor.toString());
        ctx.fillStyle = gradient;
        ctx.fillRect(0, 0, width, height);
        ctx.shadowColor = 'rgba(0, 0, 0, 0.3)';
        ctx.shadowBlur = 6;
        ctx.shadowOffsetX = 3;
        ctx.shadowOffsetY = 3;
        this.fillRect(box(logoPos.x, logoPos.y, logoSize.x, logoSize.y), DefaultColors.LightBlue);
        this.fillRect(box(logoPos.x + step * 0.8, logoPos.y + step * 0.8, logoSize.x - step * 1.6, logoSize.y - step * 1.6), DefaultColors.DarkBlue);
        ctx.fillStyle = DefaultColors.White.toString();
        this.stroke(logoPos.x + step * 2, logoPos.y + step * 7, [
            vec2(0, step * 0.2),
            vec2(0, -step * 5),
            vec2(step * 4, -step * 5),
            vec2(step * 4, -step),
            vec2(step * 1.25, -step),
            vec2(step * 1.25, step * 0.2)
        ], 0);
        ctx.fill();
        this.fillRect(box((-step * 1.5) / 2 + logoPos.x + logoSize.x / 2, (-step * 1.5) / 2 + logoPos.y + logoSize.y / 2, step * 1.5, step * 1.5), DefaultColors.LightBlue);
        this.text(p < 1 ? 'Loading assets...' : 'Ready!', vec2(width / 2, height - height * 0.05), DefaultColors.White, '1em', 'center', 'bottom');
        // Progress bar
        // this.fillRect(box(0, height - height * 0.015, width, height * 0.015), backgroundColor)
        this.fillRect(box(0, height - height * 0.015, width * p, height * 0.015), secondaryColor);
        ctx.restore();
    }
}

/******************************************************************************
Copyright (c) Microsoft Corporation.

Permission to use, copy, modify, and/or distribute this software for any
purpose with or without fee is hereby granted.

THE SOFTWARE IS PROVIDED "AS IS" AND THE AUTHOR DISCLAIMS ALL WARRANTIES WITH
REGARD TO THIS SOFTWARE INCLUDING ALL IMPLIED WARRANTIES OF MERCHANTABILITY
AND FITNESS. IN NO EVENT SHALL THE AUTHOR BE LIABLE FOR ANY SPECIAL, DIRECT,
INDIRECT, OR CONSEQUENTIAL DAMAGES OR ANY DAMAGES WHATSOEVER RESULTING FROM
LOSS OF USE, DATA OR PROFITS, WHETHER IN AN ACTION OF CONTRACT, NEGLIGENCE OR
OTHER TORTIOUS ACTION, ARISING OUT OF OR IN CONNECTION WITH THE USE OR
PERFORMANCE OF THIS SOFTWARE.
***************************************************************************** */
/* global Reflect, Promise, SuppressedError, Symbol */


function __awaiter(thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
}

function __classPrivateFieldGet(receiver, state, kind, f) {
    if (kind === "a" && !f) throw new TypeError("Private accessor was defined without a getter");
    if (typeof state === "function" ? receiver !== state || !f : !state.has(receiver)) throw new TypeError("Cannot read private member from an object whose class did not declare it");
    return kind === "m" ? f : kind === "a" ? f.call(receiver) : f ? f.value : state.get(receiver);
}

typeof SuppressedError === "function" ? SuppressedError : function (error, suppressed, message) {
    var e = new Error(message);
    return e.name = "SuppressedError", e.error = error, e.suppressed = suppressed, e;
};

var _Input_instances, _Input_onKeyDown, _Input_onKeyUp, _Input_onMouseDown, _Input_onMouseUp, _Input_onMouseMove, _Input_onWheel, _Input_getMouseScreenPos;
const isTouchDevice = window.ontouchstart !== undefined;
// @todo: Add support for touch events and gamepad input.
/**
 * Represents the Input class.
 */
class Input {
    /**
     * Creates an instance of the Input class.
     * @param {Game} game - The game instance.
     */
    constructor(game) {
        _Input_instances.add(this);
        this.game = game;
        /** The mouse wheel value. */
        this.mouseWheel = 0;
        /** The mouse screen position. */
        this.mouseScreenPos = vec2();
        /** The keyboard data. */
        this.keyboardData = {};
        /** The pointer data. */
        this.pointerData = {};
        /**
         * Handles the keydown event.
         * @private
         * @param {KeyboardEvent} e - The keyboard event.
         */
        _Input_onKeyDown.set(this, (e) => {
            if (!e.repeat) {
                this.keyboardData[e.code] = 3;
            }
        }
        /**
         * Handles the keyup event.
         * @private
         * @param {KeyboardEvent} e - The keyboard event.
         */
        );
        /**
         * Handles the keyup event.
         * @private
         * @param {KeyboardEvent} e - The keyboard event.
         */
        _Input_onKeyUp.set(this, (e) => {
            this.keyboardData[e.code] = 4;
        }
        /**
         * Handles the mousedown event.
         * @private
         * @param {MouseEvent} e - The mouse event.
         */
        );
        document.addEventListener('keydown', e => __classPrivateFieldGet(this, _Input_onKeyDown, "f").call(this, e), false);
        document.addEventListener('keyup', e => __classPrivateFieldGet(this, _Input_onKeyUp, "f").call(this, e), false);
        document.addEventListener('mousedown', e => __classPrivateFieldGet(this, _Input_instances, "m", _Input_onMouseDown).call(this, e));
        document.addEventListener('mousemove', e => __classPrivateFieldGet(this, _Input_instances, "m", _Input_onMouseMove).call(this, e));
        document.addEventListener('mouseup', e => __classPrivateFieldGet(this, _Input_instances, "m", _Input_onMouseUp).call(this, e));
        document.addEventListener('wheel', e => __classPrivateFieldGet(this, _Input_instances, "m", _Input_onWheel).call(this, e));
    }
    /**
     * Checks if a key is currently down.
     * @param {string} key - The key to check.
     * @returns {number} 1 if the key is down, otherwise 0.
     */
    keyIsDown(key) {
        return this.keyboardData[key] & 1;
    }
    /**
     * Checks if a key was pressed.
     * @param {string} key - The key to check.
     * @returns {number} 1 if the key was pressed, otherwise 0.
     */
    keyWasPressed(key) {
        return this.keyboardData[key] & 2 ? 1 : 0;
    }
    /**
     * Checks if a key was released.
     * @param {string} key - The key to check.
     * @returns {number} 1 if the key was released, otherwise 0.
     */
    keyWasReleased(key) {
        return this.keyboardData[key] & 4 ? 1 : 0;
    }
    /**
     * Checks if a mouse button is currently down.
     * @param {number} button - The button to check.
     * @returns {number} 1 if the button is down, otherwise 0.
     */
    mouseIsDown(button) {
        return this.pointerData[button] & 1;
    }
    /**
     * Checks if a mouse button was pressed.
     * @param {number} button - The button to check.
     * @returns {number} 1 if the button was pressed, otherwise 0.
     */
    mouseWasPressed(button) {
        return this.pointerData[button] & 2 ? 1 : 0;
    }
    /**
     * Checks if a mouse button was released.
     * @param {number} button - The button to check.
     * @returns {number} 1 if the button was released, otherwise 0.
     */
    mouseWasReleased(button) {
        return this.pointerData[button] & 4 ? 1 : 0;
    }
    /**
     * Clears the input data.
     */
    clearInput() {
        this.keyboardData = {};
        this.pointerData = {};
    }
    /**
     * Updates the input state.
     */
    update() {
        // clear input when lost focus (prevent stuck keys)
        isTouchDevice || document.hasFocus() || this.clearInput();
    }
    /**
     * Performs post-update operations on the input state.
     */
    postUpdate() {
        for (const i in this.keyboardData) {
            this.keyboardData[i] &= 1;
        }
        for (const i in this.pointerData) {
            this.pointerData[i] &= 1;
        }
        this.mouseWheel = 0;
    }
}
_Input_onKeyDown = new WeakMap(), _Input_onKeyUp = new WeakMap(), _Input_instances = new WeakSet(), _Input_onMouseDown = function _Input_onMouseDown(e) {
    this.pointerData[e.button] = 3;
    __classPrivateFieldGet(this, _Input_instances, "m", _Input_onMouseMove).call(this, e);
    e.button && e.preventDefault();
}, _Input_onMouseUp = function _Input_onMouseUp(e) {
    this.pointerData[e.button] = (this.pointerData[e.button] & 2) | 4;
}, _Input_onMouseMove = function _Input_onMouseMove(e) {
    this.mouseScreenPos = __classPrivateFieldGet(this, _Input_instances, "m", _Input_getMouseScreenPos).call(this, vec2(e.x, e.y));
}, _Input_onWheel = function _Input_onWheel(e) {
    e.ctrlKey || (this.mouseWheel = Math.sign(e.deltaY));
}, _Input_getMouseScreenPos = function _Input_getMouseScreenPos(pos) {
    const { canvas } = this.game;
    const rect = canvas.getBoundingClientRect();
    return vec2(canvas.width, canvas.height).multiply(vec2(percent(pos.x, rect.left, rect.right), percent(pos.y, rect.top, rect.bottom)));
};

/**
 * Represents a timer used in a game.
 */
class Timer {
    /**
     * Creates a new Timer instance.
     * @param game The game object.
     * @param t The initial time value for the timer.
     */
    constructor(game, t) {
        this.game = game;
        this.time = t === undefined ? undefined : game.time + t;
        this.ts = t;
    }
    /**
     * Sets the time value for the timer.
     * @param t The time value to set.
     */
    set(t = 0) {
        this.time = this.game.time + t;
        this.ts = t;
    }
    /**
     * Unsets the time value for the timer.
     */
    unset() {
        this.time = undefined;
    }
    /**
     * Checks if the timer is set.
     * @returns True if the timer is set, false otherwise.
     */
    isSet() {
        return this.time !== undefined;
    }
    /**
     * Checks if the timer is active.
     * @returns True if the timer is active, false otherwise.
     */
    isActive() {
        return this.time !== undefined && this.game.time <= this.time;
    }
    /**
     * Checks if the timer is done.
     * @returns True if the timer is done, false otherwise.
     */
    isDone() {
        return this.time !== undefined && this.game.time > this.time;
    }
    /**
     * Gets the elapsed time since the timer was set.
     * @returns The elapsed time in milliseconds.
     */
    getTime() {
        return this.time !== undefined ? this.game.time - this.time : 0;
    }
    /**
     * Gets the percentage of time remaining for the timer.
     * @returns The percentage value between 0 and 100.
     */
    getPercent() {
        return this.time !== undefined && this.ts !== undefined ? percent(this.time - this.game.time, this.ts, 0) : 0;
    }
}

/**
 * Represents a camera object used for rendering and controlling the view of a scene.
 */
class Camera {
    /**
     * Camera constructor
     * @param scene Scene object
     */
    constructor(scene) {
        this.scene = scene;
        /** Camera position */
        this.pos = vec2();
        /** Camera shake offset */
        this.offset = vec2();
        /** Camera scale (zoom) */
        this.scale = 1;
        /** Camera horizontal and vertical speed scale (percent). 0 - no movement, 1 - instant movement */
        this.speed = vec2(1);
        /** Scrolling mode (smooth or switch between views without scrolling) */
        this.scrolling = true;
        /** Is camera shaking */
        this.isShaking = false;
        /** Camera bounds. If true, camera will not go outside the game world. */
        this.bounded = true;
        /** Shake intensity */
        this.shakeIntensity = vec2();
        /** Shake duration */
        this.shakeDuration = 0;
        /** Shake elapsed time*/
        this.shakeElapsed = 0;
    }
    /**
     * Set camera position
     * @param pos Position vector
     */
    setPos(pos) {
        this.followEntity = undefined;
        this.pos = pos;
    }
    /**
     * Set camera scale
     * @param scale
     */
    setScale(scale) {
        this.scale = scale;
    }
    /**
     * Sets the speed of the camera.
     * @param speed - The speed value or a vector representing the speed.
     */
    setSpeed(speed) {
        this.speed = typeof speed === 'number' ? vec2(speed) : speed;
    }
    /**
     * Sets the scrolling behavior of the camera.
     * @param scrolling - A boolean value indicating whether scrolling is enabled or disabled.
     */
    setScrolling(scrolling) {
        this.scrolling = scrolling;
    }
    /**
     * Sets the entity to follow.
     * @param follow The entity to follow.
     */
    follow(follow) {
        this.followEntity = follow;
    }
    /**
     * Stops the camera from following any entity.
     */
    unfollow() {
        this.followEntity = undefined;
    }
    /**
     * Toggles the bounds of the camera.
     * @param bounded - A boolean value indicating whether the camera should be bounded or not.
     */
    toggleBounds(bounded) {
        this.bounded = bounded;
    }
    /**
     * Shakes the camera for a specified duration with a given intensity.
     * @param duration The duration of the camera shake in seconds.
     * @param intensity The intensity of the camera shake as a Vector.
     */
    shake(duration, intensity) {
        this.isShaking = true;
        this.shakeDuration = duration;
        this.shakeIntensity = vec2(intensity.x, intensity.y);
        this.offset = vec2(0);
        this.shakeElapsed = 0;
    }
    /**
     * Updates the camera position and behavior based on the current scene state.
     */
    update() {
        const { game, size, tileSize } = this.scene;
        const viewSize = vec2(game.width, game.height);
        if (this.followEntity) {
            const followRect = this.followEntity.getTranslatedBoundingRect();
            if (this.scrolling) {
                // smooth scroll
                const midPos = followRect.pos
                    .clone()
                    .invert()
                    .add(viewSize.divide(vec2(2)))
                    .subtract(followRect.size.divide(vec2(2)));
                this.pos.x = Math.ceil(lerp(this.speed.x, this.pos.x, midPos.x));
                this.pos.y = Math.ceil(lerp(this.speed.y, this.pos.y, midPos.y));
            }
            else {
                // no scrolling - switching between views
                const room = followRect.pos.divide(viewSize).floor();
                this.pos = vec2(-room.x * viewSize.x, -room.y * viewSize.y);
            }
        }
        // check bounds
        if (this.bounded) {
            const max = size.multiply(tileSize).scale(this.scale).subtract(viewSize);
            if (this.pos.x < -max.x + this.scale)
                this.pos.x = -max.x + this.scale;
            if (this.pos.y < -max.y + this.scale)
                this.pos.y = -max.y + this.scale;
            if (this.pos.x > 0)
                this.pos.x = 0;
            if (this.pos.y > 0)
                this.pos.y = 0;
        }
        // shake
        if (this.isShaking) {
            this.shakeElapsed += game.delta;
            // progress = clamp(this.shakeElapsed / this.shakeDuration, 0, 1)
            if (this.shakeElapsed < this.shakeDuration) {
                this.offset = vec2(Math.random() * this.shakeIntensity.x * viewSize.x * 2 - this.shakeIntensity.x * viewSize.x, Math.random() * this.shakeIntensity.y * viewSize.y * 2 - this.shakeIntensity.y * viewSize.y).scale(this.scale);
            }
            else {
                this.isShaking = false;
                this.offset = vec2();
            }
            this.pos = this.pos.add(this.offset);
        }
    }
}

/**
 * The `Sprite` class represents an image that can be drawn on the screen.
 * It can be animated using a specified animation.
 * @see {@link Animation}
 */
class Sprite {
    /**
     * Creates a new `Sprite` object.
     * @param entity - The entity that the sprite belongs to.
     */
    constructor(entity) {
        this.entity = entity;
        /** The current frame of the animation. */
        this.then = getPerformance();
        /** The start time of the frame. */
        this.frameStart = getPerformance();
        /** The current frame of the animation.*/
        this.animFrame = 0;
    }
    /**
     * Animates the sprite based on the current animation settings.
     */
    animate() {
        const { animation } = this.entity;
        if (animation) {
            this.animFrame = this.animFrame || 0;
            this.frameStart = getPerformance();
            if (this.animation !== animation) {
                this.animation = animation;
                this.animFrame = 0;
            }
            const duration = animation.strip
                ? animation.strip.duration
                : (animation.frames && animation.frames[this.animFrame][2]) || 0;
            const framesCount = animation.strip
                ? animation.strip.frames
                : (animation.frames && animation.frames.length) || 0;
            if (this.frameStart - this.then > duration) {
                if (this.animFrame <= framesCount && animation.loop) {
                    this.animFrame = normalize(this.animFrame + 1, 0, framesCount);
                }
                else if (this.animFrame < framesCount - 1 && !animation.loop) {
                    this.animFrame += 1;
                }
                this.then = getPerformance();
            }
        }
    }
    /**
     * Draws the sprite.
     */
    draw() {
        const { animation, animFrame } = this;
        const { angle, color, scene, shape, flipH, flipV } = this.entity;
        const { game, camera } = scene;
        const scale = vec2(camera.scale);
        const image = this.entity.image && scene.game.getImage(this.entity.image);
        const boundingRect = this.entity.getRelativeBoundingRect();
        if (image) {
            if (animation) {
                const { frames, strip, width, height } = animation;
                const frame = (frames && frames[animFrame]) || [0, 0];
                const clip = strip ? vec2(strip.x + animFrame * width, strip.y) : vec2(frame[0], frame[1]);
                const offset = (animation === null || animation === void 0 ? void 0 : animation.offset) ? vec2(...animation.offset).scale(camera.scale) : vec2(0, 0);
                const rect = new Box(boundingRect.pos.add(offset), vec2(width, height));
                game.draw.draw2d(image, rect, scale, angle, flipH, flipV, clip);
            }
            else {
                game.draw.draw2d(image, boundingRect, scale, angle, flipH, flipV);
            }
        }
        else if (color) {
            if (shape === Shape.Ellipse) {
                game.draw.fillEllipse(boundingRect, color, angle);
            }
            else {
                game.draw.fillRect(boundingRect, color, angle);
            }
        }
    }
}

/**
 * The Entity class represents a game object in the scene.
 */
class Entity {
    /**
     * Creates a new Entity object.
     * @param scene
     * @param obj
     */
    constructor(scene, obj) {
        this.scene = scene;
        this.obj = obj;
        /** The ID of the object. */
        this.id = Date.now();
        /** Whether the object is bounded by the scene and cannot move outside. */
        this.bounded = false;
        /** Whether the object is flipped horizontally (mirrored) */
        this.flipH = false;
        /** Whether the object is flipped vertically */
        this.flipV = false;
        /** Whether the object is solid and collides with other objects */
        this.solid = true;
        /** Whether the object is visible and should be drawn */
        this.visible = true;
        /** Whether the object is active and should be updated */
        this.active = true;
        /** Whether the object is dead and should be removed */
        this.dead = false;
        /** Whether the object is static and should not move */
        this.collideTiles = true;
        /** Whether the object collides with other objects */
        this.collideObjects = true;
        /** The object the entity is standing on */
        this.onGround = false;
        /** The shape of the entity. */
        this.shape = Shape.Rectangle;
        /** Current position of the object */
        this.pos = vec2();
        /** Last position of the object */
        this.lastPos = vec2();
        /** The size of the object (scaled by tileSize) */
        this.size = vec2();
        /** The force applied to the object (acceleration) */
        this.force = vec2();
        /** The object's mass (0 is static) */
        this.mass = 1;
        /** The object's damping (0-1) */
        this.damping = 1;
        /** The object's elasticity (0-1) */
        this.elasticity = 0;
        /** The object's friction (0-1) */
        this.friction = 0.8;
        /** How much gravity affects the object */
        this.gravityScale = 1;
        /** The object's angular velocity */
        this.angleVelocity = 0;
        /** Rotation slowdown (0-1) */
        this.angleDamping = 1;
        /** Rotation angle (in radians) */
        this.angle = 0;
        /** Maximum speed */
        this.maxSpeed = 1;
        /** Time the entity was spawned */
        this.spawnTime = 0;
        /** Render order */
        this.renderOrder = 0;
        /** Custom properties of the object */
        this.properties = {};
        /**
         * The sprite of the object.
         * @see {@link Sprite}
         */
        this.sprite = new Sprite(this);
        if (obj) {
            this.id = obj.id;
            this.gid = obj.gid;
            this.type = obj.type;
            this.name = obj.name;
            this.layerId = obj.layerId;
            this.properties = obj.properties;
            // Translate TMX bounding rect into game grid
            if (obj.x && obj.y) {
                const { tileSize } = scene;
                const tmxRect = box(obj.x, obj.y, obj.width, obj.height);
                this.size = vec2(tmxRect.size.x / tileSize.x, tmxRect.size.y / tileSize.y);
                this.pos = vec2(tmxRect.pos.x / tileSize.x + this.size.x / 2, tmxRect.pos.y / tileSize.y + this.size.y / 2 - (this.gid ? this.size.y : 0));
            }
            else {
                this.size = obj.size || this.size;
                this.pos = obj.pos || this.pos;
            }
        }
        this.spawnTime = scene.game.time;
        this.angle = (obj === null || obj === void 0 ? void 0 : obj.rotation) ? deg2rad(obj.rotation) : this.angle;
        this.visible = (obj === null || obj === void 0 ? void 0 : obj.visible) !== undefined ? obj.visible : true;
        this.force = (obj === null || obj === void 0 ? void 0 : obj.force) || this.force;
        this.flipH = (obj === null || obj === void 0 ? void 0 : obj.flipH) || this.flipH;
        this.flipV = (obj === null || obj === void 0 ? void 0 : obj.flipV) || this.flipV;
        this.animation = (obj === null || obj === void 0 ? void 0 : obj.animation) || this.animation;
        this.family = (obj === null || obj === void 0 ? void 0 : obj.family) || this.family;
        this.shape = (obj === null || obj === void 0 ? void 0 : obj.shape) || this.shape;
    }
    /**
     * Marks the entity as destroyed.
     */
    destroy() {
        this.dead = true;
    }
    /**
     * Returns the alive time of the entity.
     * @returns The alive time in milliseconds.
     */
    getAliveTime() {
        return this.scene.game.time - this.spawnTime;
    }
    /**
     * Applies a force to the entity (affected by mass)
     * @param force - The force to be applied.
     */
    applyForce(force) {
        if (this.mass)
            this.force = this.force.add(force.scale(1 / this.mass));
    }
    /**
     * Checks if the entity collides with a tile.
     * @param tileId - The ID of the tile to check collision with.
     * @param pos - The position of the entity.
     * @returns True if the entity collides with the tile, false otherwise.
     */
    collideWithTile(tileId, pos) {
        return tileId > 0;
    }
    /**
     * Checks if the entity collides with a tile using raycasting.
     * @param tileId - The data of the tile to check collision with.
     * @param pos - The position of the entity.
     * @returns True if the entity collides with the tile, false otherwise.
     */
    collideWithTileRaycast(tileId, pos) {
        return tileId > 0;
    }
    /**
     * Checks if this entity collides with another entity.
     * @param entity - The entity to check collision with.
     * @returns `true` if a collision occurs, `false` otherwise.
     */
    collideWithObject(entity) {
        return true;
    }
    /**
     * Checks if the entity is currently on the screen.
     * @returns {boolean} True if the entity is on the screen, false otherwise.
     */
    onScreen() {
        return this.scene.getCameraVisibleArea().overlaps(this.pos, this.size);
    }
    /**
     * Draws the entity on the screen if it is visible and within the screen bounds.
     * If the entity has a tile object, it draws the tile object with the specified transformations.
     * Otherwise, it draws the entity's sprite.
     * If the game is in debug mode, it also displays debug information.
     */
    draw() {
        if (this.visible && this.onScreen()) {
            if (this.gid) {
                const tile = this.scene.getTileObject(this.gid);
                const scale = this.size.multiply(this.scene.tileSize).divide(tile.size).scale(this.scene.camera.scale);
                tile.draw(this.scene.getScreenPos(this.pos, this.size), this.flipH, this.flipV, this.angle, scale);
            }
            else {
                this.sprite.draw();
            }
            if (this.scene.game.debug)
                this.displayDebug();
        }
    }
    /**
     * Sets the animation for the entity.
     * @param animation - The animation to set.
     * @param flipH - (Optional) Whether to flip the animation horizontally. Default is false.
     * @param flipV - (Optional) Whether to flip the animation vertically. Default is false.
     */
    setAnimation(animation, flipH = false, flipV = false) {
        this.flipH = flipH;
        this.flipV = flipV;
        this.animation = animation;
    }
    /**
     * Gets the current animation frame of the entity.
     * If the sprite is defined, it returns the animation frame of the sprite.
     * Otherwise, it returns 0.
     *
     * @returns The current animation frame.
     */
    getAnimationFrame() {
        var _a;
        return ((_a = this.sprite) === null || _a === void 0 ? void 0 : _a.animFrame) || 0;
    }
    /**
     * Sets the animation frame for the entity's sprite.
     * @param frame - The frame number to set.
     */
    setAnimationFrame(frame) {
        this.sprite.animFrame = frame;
    }
    /**
     * Returns the translated position rectangle of the entity.
     * The translated position rectangle is calculated based on the entity's position in the scene.
     * @returns The translated position rectangle of the entity.
     */
    getTranslatedBoundingRect() {
        return new Box(this.scene.getScreenPos(this.pos, this.size), this.size.clone().multiply(this.scene.tileSize).scale(this.scene.camera.scale));
    }
    /**
     * Returns the relative bounding rectangle of the entity.
     * The relative bounding rectangle is calculated by translating the entity's bounding rectangle
     * and moving it based on the position of the scene's camera.
     * @returns The relative bounding rectangle of the entity.
     */
    getRelativeBoundingRect() {
        return this.getTranslatedBoundingRect().move(this.scene.camera.pos);
    }
    /**
     * Checks if the entity is overlapping with a given position and size.
     * @param pos - The position vector of the other object.
     * @param size - The size vector of the other object.
     * @returns True if the entity is overlapping with the other object, false otherwise.
     */
    isOverlapping(pos, size = vec2()) {
        return (Math.abs(this.pos.x - pos.x) * 2 < this.size.x + size.x &&
            Math.abs(this.pos.y - pos.y) * 2 < this.size.y + size.y);
    }
    /**
     * Updates the entity's position, velocity, and handles collisions with other objects and tiles.
     * This method applies physics, friction, and collision resolution to the entity.
     * It also updates the entity's animation.
     *
     * Based on LittleJS by Frank Force.
     * @see {@link https://github.com/KilledByAPixel/LittleJS/blob/main/src/engineObject.js}
     */
    update() {
        const { scene } = this;
        this.sprite.animate();
        // Kill if TTL is up
        if (this.ttl && Math.min(this.getAliveTime() / this.ttl, 1) === 1) {
            this.destroy();
        }
        // Obey speed limit
        this.force.x = clamp(this.force.x, -this.maxSpeed, this.maxSpeed);
        this.force.y = clamp(this.force.y, -this.maxSpeed, this.maxSpeed);
        // Do not perform physics on static objects
        if (!this.mass)
            return;
        this.lastPos = this.pos.clone();
        this.force.y += scene.gravity * this.gravityScale;
        this.pos.x += this.force.x *= this.damping;
        this.pos.y += this.force.y *= this.damping;
        this.angle += this.angleVelocity *= this.angleDamping;
        // Check bounds
        if (this.bounded) {
            this.pos.x < 0 && (this.pos.x = 0);
            this.pos.y < 0 && (this.pos.y = 0);
            this.pos.x > scene.size.x && (this.pos.x = scene.size.x);
            this.pos.y > scene.size.y && (this.pos.y = scene.size.y);
        }
        const wasMovingDown = this.force.y >= 0;
        if (this.onGround) {
            const groundSpeed = this.onGround instanceof Entity ? this.onGround.force.x : 0;
            this.force.x = groundSpeed + (this.force.x - groundSpeed) * this.friction;
            this.onGround = false;
        }
        if (this.collideTiles) {
            if (scene.testTileCollision(this.pos, this.size, this)) {
                if (!scene.testTileCollision(this.lastPos, this.size, this)) {
                    const isBlockedY = scene.testTileCollision(vec2(this.lastPos.x, this.pos.y), this.size, this);
                    const isBlockedX = scene.testTileCollision(vec2(this.pos.x, this.lastPos.y), this.size, this);
                    if (isBlockedY || !isBlockedX) {
                        this.onGround = wasMovingDown;
                        this.force.y *= -this.elasticity;
                        const o = ((this.lastPos.y - this.size.y / 2) | 0) - (this.lastPos.y - this.size.y / 2);
                        if (o < 0 && o > this.damping * this.force.y + scene.gravity * this.gravityScale)
                            this.force.y = this.damping ? (o - scene.gravity * this.gravityScale) / this.damping : 0;
                        this.pos.y = this.lastPos.y;
                    }
                    if (isBlockedX) {
                        this.pos.x = this.lastPos.x;
                        this.force.x *= -this.elasticity;
                    }
                }
            }
        }
        if (this.collideObjects) {
            const epsilon = 0.001; // necessary to push slightly outside of the collision
            const collidingObjects = scene.objects.filter(o => o.visible && o.collideObjects);
            for (const o of collidingObjects) {
                if ((!this.solid && !o.solid) || o.dead || o == this)
                    continue;
                if (!o.isOverlapping(this.pos, this.size))
                    continue;
                const collide1 = this.collideWithObject(o);
                const collide2 = o.collideWithObject(this);
                if (!collide1 || !collide2)
                    continue;
                if (o.isOverlapping(this.lastPos, this.size)) {
                    // if already was touching, try to push away
                    const deltaPos = this.lastPos.subtract(o.pos);
                    const length = deltaPos.length();
                    const pushAwayAccel = 0.001; // push away if already overlapping
                    const force = length < 0.01 ? randVector(pushAwayAccel) : deltaPos.scale(pushAwayAccel / length);
                    this.force = this.force.add(force);
                    if (o.mass)
                        o.force = o.force.subtract(force);
                    continue;
                }
                const sizeBoth = this.size.add(o.size);
                const smallStepUp = (this.lastPos.y - o.pos.y) * 2 > sizeBoth.y + scene.gravity;
                const isBlockedX = Math.abs(this.lastPos.y - o.pos.y) * 2 < sizeBoth.y;
                const isBlockedY = Math.abs(this.lastPos.x - o.pos.x) * 2 < sizeBoth.x;
                const elasticity = Math.max(this.elasticity, o.elasticity);
                if (smallStepUp || isBlockedY || !isBlockedX) {
                    this.pos.y = o.pos.y + (sizeBoth.y / 2 + epsilon) * Math.sign(this.lastPos.y - o.pos.y);
                    if ((o.onGround && wasMovingDown) || !o.mass) {
                        if (wasMovingDown)
                            this.onGround = o;
                        this.force.y *= -elasticity;
                    }
                    else if (o.mass) {
                        // inelastic collision
                        const inelastic = (this.mass * this.force.y + o.mass * o.force.y) / (this.mass + o.mass);
                        // elastic collision
                        const elastic0 = (this.force.y * (this.mass - o.mass)) / (this.mass + o.mass) +
                            (o.force.y * 2 * o.mass) / (this.mass + o.mass);
                        const elastic1 = (o.force.y * (o.mass - this.mass)) / (this.mass + o.mass) +
                            (this.force.y * 2 * this.mass) / (this.mass + o.mass);
                        // lerp betwen elastic or inelastic based on elasticity
                        this.force.y = lerp(elasticity, inelastic, elastic0);
                        o.force.y = lerp(elasticity, inelastic, elastic1);
                    }
                }
                if (!smallStepUp && isBlockedX) {
                    this.pos.x = o.pos.x + (sizeBoth.x / 2 + epsilon) * Math.sign(this.lastPos.x - o.pos.x);
                    if (o.mass) {
                        // inelastic collision
                        const inelastic = (this.mass * this.force.x + o.mass * o.force.x) / (this.mass + o.mass);
                        // elastic collision
                        const elastic0 = (this.force.x * (this.mass - o.mass)) / (this.mass + o.mass) +
                            (o.force.x * 2 * o.mass) / (this.mass + o.mass);
                        const elastic1 = (o.force.x * (o.mass - this.mass)) / (this.mass + o.mass) +
                            (this.force.x * 2 * this.mass) / (this.mass + o.mass);
                        // lerp betwen elastic or inelastic based on elasticity
                        this.force.x = lerp(elasticity, inelastic, elastic0);
                        o.force.x = lerp(elasticity, inelastic, elastic1);
                    } // bounce if other object is fixed
                    else
                        this.force.x *= -elasticity;
                }
            }
        }
    }
    /**
     * Displays debug information about the entity.
     */
    displayDebug() {
        const rect = this.getRelativeBoundingRect();
        const { draw, primaryColor } = this.scene.game;
        const { angle, id, type, visible, force, pos } = this;
        const { Cyan, LightGreen, Red } = DefaultColors;
        const { pos: { x, y }, size } = rect;
        const s1 = size.x / 2;
        const x1 = x + s1;
        const fs = '1em';
        draw.outline(rect, visible ? LightGreen : Cyan, 1);
        draw.text(`${type || id || ''}[${angle.toFixed(2)}]`, vec2(x1, y - 14), primaryColor, fs, 'center', 'top', true);
        draw.text(`x:${pos.x.toFixed(1)}`, vec2(x1 - s1 - 2, y), primaryColor, fs, 'right', 'top', true);
        draw.text(`y:${pos.y.toFixed(1)}`, vec2(x1 - s1 - 2, y + 14), primaryColor, fs, 'right', 'top', true);
        Math.abs(force.x) > 0.012 &&
            draw.text(`x:${force.x.toFixed(3)}`, vec2(x1 + s1 + 2, y), Red, fs, 'left', 'top', true);
        Math.abs(force.y) > 0.012 &&
            draw.text(`y:${force.y.toFixed(3)}`, vec2(x1 + s1 + 2, y + 14), Red, fs, 'left', 'top', true);
    }
}

/**
 * Represents a particle in the Platfuse game engine.
 */
class Particle extends Entity {
    /**
     * Creates a new Particle instance.
     * @param scene The scene the particle belongs to.
     * @param obj Additional properties for the particle.
     */
    constructor(scene, obj) {
        super(scene, obj);
        this.scene = scene;
        /** The type of the particle. */
        this.type = 'Platfuse.Particle';
        /** The shape of the particle. */
        this.shape = Shape.Rectangle;
        /** The starting color of the particle. */
        this.colorStart = new Color();
        /** The color delta of the particle. */
        this.colorEnd = new Color();
        /** The time to live (in seconds) of the particle. */
        this.ttl = 0;
        /** The starting size of the particle. */
        this.sizeStart = 0.1;
        /** The size delta of the particle. */
        this.sizeDelta = 0;
        /** The rate at which the particle fades. */
        this.fadeRate = 0.1;
        /** The scale of the particle's stretch effect. */
        this.stretchScale = 0;
        this.pos = (obj === null || obj === void 0 ? void 0 : obj.pos) || vec2();
    }
    /**
     * Draws the particle on the canvas.
     */
    draw() {
        const { time } = this.scene.game;
        const p = Math.min((time - this.spawnTime) / this.ttl, 1);
        const radius = this.sizeStart + p * this.sizeDelta;
        const fading = this.fadeRate / 2;
        this.size = vec2(radius);
        this.color = new Color(this.colorStart.r + p * this.colorEnd.r, this.colorStart.g + p * this.colorEnd.g, this.colorStart.b + p * this.colorEnd.b, (this.colorStart.a + p * this.colorEnd.a) *
            (p < fading ? p / fading : p > 1 - fading ? (1 - p) / fading : 1));
        if (this.stretchScale) {
            const speed = this.force.length();
            if (speed) {
                const direction = this.force.scale(1 / speed);
                const trailLength = speed * this.stretchScale;
                this.size.y = Math.max(this.size.x, trailLength);
                this.angle = direction.angle();
            }
        }
        if (p === 1) {
            this.destroy();
        }
        super.draw();
    }
    /**
     * Do not display debug information for the particle.
     */
    displayDebug() { }
}

/**
 * Represents an emitter object that spawns particles in a scene.
 */
class Emitter extends Entity {
    /**
     * Creates a new Emitter instance.
     * @param scene - The scene the emitter belongs to.
     * @param obj - Additional properties for the emitter.
     */
    constructor(scene, obj) {
        super(scene, obj);
        this.scene = scene;
        this.obj = obj;
        /** The type of the emitter. */
        this.type = 'Platfuse.Emitter';
        /** The shape of the particle. */
        this.shape = Shape.Rectangle;
        /** The angle to emit the particles. */
        this.angle = 0;
        /** How long the emitter stays alive (0 is forever). */
        this.emitTime = 0;
        /** How many particles per second to spawn. If set to 0, no particles will be emitted. */
        this.emitRate = 100;
        /** The local angle to apply velocity to particles from the emitter. */
        this.emitConeAngle = Math.PI;
        /** The color at the start of the particle's life. It is randomized between start colors. */
        this.colorStart = new Color();
        /** The color at the end of the particle's life. It is randomized between end colors. */
        this.colorEnd = new Color();
        /** How long particles live. */
        this.ttl = 0.5;
        /** The size of particles at the start. */
        this.sizeStart = 0.1;
        /** The size of particles at the end. */
        this.sizeEnd = 1;
        /** The speed of particles when spawned. */
        this.speed = 0.1;
        /** How much to dampen particle speed. */
        this.damping = 1;
        /** How much to dampen particle angular speed. */
        this.angleDamping = 1;
        /** How much gravity affects particles. */
        this.gravityScale = 0;
        /** The cone for the start particle angle. */
        this.particleConeAngle = Math.PI;
        /** How quickly particles fade in at the start/end in percent of life. */
        this.fadeRate = 0.1;
        /** The randomness percent. */
        this.randomness = 0.2;
        /** Whether particles collide against tiles. */
        this.collideTiles = false;
        /** Whether particles collide against objects. */
        this.collideObjects = false;
        /** The render order of particles. */
        this.renderOrder = 0;
        /** If set, the particle is drawn as a trail, stretched in the direction of velocity. */
        this.stretchScale = 0;
        /** Time buffer for emit rate. */
        this.timeBuffer = 0;
        this.pos = (obj === null || obj === void 0 ? void 0 : obj.pos) || vec2();
        this.angle = (obj === null || obj === void 0 ? void 0 : obj.angle) || 0;
        this.emitSize = (obj === null || obj === void 0 ? void 0 : obj.emitSize) || 0;
        this.emitTime = (obj === null || obj === void 0 ? void 0 : obj.emitTime) || 0;
        this.emitRate = (obj === null || obj === void 0 ? void 0 : obj.emitRate) || this.emitRate;
        this.elasticity = (obj === null || obj === void 0 ? void 0 : obj.elasticity) || this.elasticity;
        this.emitConeAngle = (obj === null || obj === void 0 ? void 0 : obj.emitConeAngle) || this.emitConeAngle;
        this.colorStart = (obj === null || obj === void 0 ? void 0 : obj.colorStart) || this.colorStart;
        this.colorEnd = (obj === null || obj === void 0 ? void 0 : obj.colorEnd) || this.colorEnd;
        this.ttl = (obj === null || obj === void 0 ? void 0 : obj.ttl) || this.ttl;
        this.sizeStart = (obj === null || obj === void 0 ? void 0 : obj.sizeStart) || this.sizeStart;
        this.sizeEnd = (obj === null || obj === void 0 ? void 0 : obj.sizeEnd) || this.sizeEnd;
        this.speed = (obj === null || obj === void 0 ? void 0 : obj.speed) || this.speed;
        this.damping = (obj === null || obj === void 0 ? void 0 : obj.damping) || this.damping;
        this.angleDamping = (obj === null || obj === void 0 ? void 0 : obj.angleDamping) || this.angleDamping;
        this.gravityScale = (obj === null || obj === void 0 ? void 0 : obj.gravityScale) || this.gravityScale;
        this.particleConeAngle = (obj === null || obj === void 0 ? void 0 : obj.particleConeAngle) || this.particleConeAngle;
        this.fadeRate = (obj === null || obj === void 0 ? void 0 : obj.fadeRate) || this.fadeRate;
        this.randomness = (obj === null || obj === void 0 ? void 0 : obj.randomness) || this.randomness;
        this.collideTiles = !!(obj === null || obj === void 0 ? void 0 : obj.collideTiles);
        this.collideObjects = !!(obj === null || obj === void 0 ? void 0 : obj.collideObjects);
        this.stretchScale = (obj === null || obj === void 0 ? void 0 : obj.stretchScale) || this.stretchScale;
        this.elasticity = (obj === null || obj === void 0 ? void 0 : obj.elasticity) || this.elasticity;
        this.shape = (obj === null || obj === void 0 ? void 0 : obj.shape) || this.shape;
    }
    /**
     * Updates the emitter.
     * @remarks
     * This method is responsible for updating the emitter's behavior, such as emitting particles
     * based on the emit rate and time. If the emitter's alive time exceeds the emit time,
     * the emitter is destroyed.
     */
    update() {
        const { delta } = this.scene.game;
        if (!this.emitTime || this.getAliveTime() <= this.emitTime) {
            if (this.emitRate) {
                const rate = 1 / this.emitRate;
                for (this.timeBuffer += delta; this.timeBuffer > 0; this.timeBuffer -= rate)
                    this.emitParticle();
            }
        }
        else
            this.destroy();
    }
    /**
     * Randomizes the given value by applying randomness.
     * @param v - The value to be randomized.
     * @returns The randomized value.
     */
    randomize(v) {
        return v + v * rand(this.randomness, -this.randomness);
    }
    /**
     * Emits a particle from the emitter.
     */
    emitParticle() {
        const pos = this.pos.add(this.emitSize instanceof Vector
            ? vec2(rand(-0.5, 0.5), rand(-0.5, 0.5)).multiply(this.emitSize).rotate(this.angle)
            : randPointOnCircle(this.emitSize / 2));
        const angle = this.angle + rand(this.particleConeAngle, -this.particleConeAngle);
        const particle = new Particle(this.scene, Object.assign(Object.assign({}, this.obj), { angle, pos }));
        const ttl = this.randomize(this.ttl);
        const sizeStart = this.randomize(this.sizeStart);
        const sizeEnd = this.randomize(this.sizeEnd);
        const speed = this.randomize(this.speed);
        const coneAngle = rand(this.emitConeAngle, -this.emitConeAngle);
        const forceAngle = this.angle + coneAngle;
        particle.shape = this.shape;
        particle.colorStart = this.colorStart;
        particle.colorEnd = this.colorEnd.subtract(this.colorStart);
        particle.renderOrder = this.renderOrder;
        particle.damping = this.damping;
        particle.angleDamping = this.angleDamping;
        particle.elasticity = this.elasticity;
        particle.friction = this.friction;
        particle.gravityScale = this.gravityScale;
        particle.collideTiles = this.collideTiles;
        particle.collideObjects = this.collideObjects;
        particle.force = vec2().setAngle(forceAngle, speed);
        particle.ttl = ttl;
        particle.sizeStart = sizeStart;
        particle.sizeDelta = sizeEnd - sizeStart;
        particle.fadeRate = this.fadeRate;
        particle.stretchScale = this.stretchScale;
        particle.layerId = this.layerId;
        this.scene.objects.push(particle);
    }
    /**
     * Draws the emitter.
     */
    draw() { }
}

/**
 * Asynchronously preloads a collection of assets.
 * @param assets - The collection of assets to preload.
 * @param indicator - A callback function that receives the progress of the preload operation.
 * @returns A promise that resolves to an object containing the preloaded assets.
 */
function preload(assets, indicator) {
    return __awaiter(this, void 0, void 0, function* () {
        const count = Object.keys(assets).length;
        let loadedCount = 0;
        const load = (key) => {
            const src = assets[key];
            return new Promise(res => {
                if (/\.(gif|jpe?g|png|webp|bmp)$/i.test(src) || /(data:image\/[^;]+;base64[^"]+)$/i.test(src)) {
                    const img = new Image();
                    img.src = src;
                    img.onload = () => {
                        indicator(++loadedCount / count);
                        return res(img);
                    };
                }
                else if (/\.(webm|mp3|wav)$/i.test(src) || /(data:audio\/[^;]+;base64[^"]+)$/i.test(src)) {
                    const audio = new Howl({ src });
                    audio.once('load', () => {
                        indicator(++loadedCount / count);
                        return res(audio);
                    });
                }
                else
                    return Promise.resolve();
            });
        };
        return Promise.resolve(Object.assign({}, ...(yield Promise.all(Object.keys(assets).map((key) => __awaiter(this, void 0, void 0, function* () {
            return ({
                [key]: yield load(key)
            });
        }))))));
    });
}

class Layer {
    /**
     * Creates a new layer.
     * @param scene
     * @param layerData
     */
    constructor(scene, layerData) {
        this.scene = scene;
        /** The unique identifier of the layer. */
        this.id = Date.now();
        /** The type of the layer. */
        this.type = NodeType.Custom;
        /** The size of the layer. */
        this.size = vec2();
        /** The render order of the layer. */
        this.renderOrder = 0;
        /** Flag indicating whether the layer is visible. */
        this.visible = true;
        /** The properties of the layer. */
        this.properties = {};
        if (layerData) {
            this.id = layerData.id;
            this.name = layerData.name;
            this.type = layerData.type;
            this.size = vec2(layerData.width, layerData.height);
            this.visible = layerData.visible === undefined ? true : !!layerData.visible;
            this.properties = (layerData === null || layerData === void 0 ? void 0 : layerData.properties) || {};
            if (layerData.type === NodeType.Layer) {
                this.data = layerData === null || layerData === void 0 ? void 0 : layerData.data;
                this.renderTilesToLayerCanvas();
            }
        }
        else {
            this.type = NodeType.Custom;
        }
    }
    /**
     * Renders the tiles to the layer canvas.
     */
    renderTilesToLayerCanvas() {
        if (this.data) {
            this.layerCanvas = document.createElement('canvas');
            this.layerCanvas.width = this.size.x * this.scene.tileSize.x;
            this.layerCanvas.height = this.size.y * this.scene.tileSize.y;
            this.layerContext = this.layerCanvas.getContext('2d');
            this.data.forEach((tileId, index) => tileId && this.drawTileAt(tileId, index));
        }
    }
    /**
     * Updates the layer.
     */
    update() { }
    /**
     * Post-update method.
     * This method is called after the update and draw.
     * It can be used to perform additional operations after the update.
     */
    postUpdate() { }
    /**
     * Draws a tile at the specified index on the layer.
     * @param tileId - The ID of the tile to draw.
     * @param index - The index of the tile on the layer.
     */
    drawTileAt(tileId, index) {
        if (tileId) {
            const tile = this.scene.getTileObject(tileId);
            const pos = vec2(index % this.size.x, Math.floor(index / this.size.x)).multiply(this.scene.tileSize);
            const { H, V } = getFlips(tileId) || { H: false, V: false };
            const { image, tilewidth, tileheight } = tile.tileset;
            const { game } = this.scene;
            // render static (non-animated) tiles to layer canvas
            if (!tile.animated) {
                game.draw.draw2d(game.getImage(image.source), new Box(pos, vec2(tilewidth, tileheight)), vec2(1), 0, H, V, tile.getSpriteClip(), this.layerContext);
            }
        }
    }
    /**
     * Retrieves the tile at the specified position.
     * @param pos - The position of the tile.
     * @returns The tile at the specified position, or `null` if the position is out of range
     *          or the data is not available.
     */
    getTile(pos) {
        return (pos.inRange(this.size) && this.data && this.data[pos.x + this.size.x * pos.y]) || null;
    }
    /**
     * Sets the tile at the specified position.
     * @param pos - The position of the tile.
     * @param tileId - The ID of the tile to set.
     */
    setTile(pos, tileId) {
        if (this.data && pos.inRange(this.size)) {
            const index = pos.x + this.size.x * pos.y;
            this.data[index] = tileId;
            this.drawTileAt(tileId, index);
        }
    }
    /**
     * Clears the tile at the specified position.
     * @param pos - The position of the tile to clear.
     */
    clearTile(pos) {
        var _a;
        if (this.data && pos.inRange(this.size)) {
            this.data[pos.x + this.size.x * pos.y] = null;
            (_a = this.layerContext) === null || _a === void 0 ? void 0 : _a.clearRect(pos.x * this.scene.tileSize.x, pos.y * this.scene.tileSize.y, this.scene.tileSize.x, this.scene.tileSize.y);
        }
    }
    /**
     * Toggles the visibility of the layer.
     * @param toggle - A boolean value indicating whether to show or hide the layer.
     */
    toggleVisibility(toggle) {
        this.visible = toggle;
    }
    /**
     * Iterates over each visible tile and executes the provided callback function.
     *
     * @param fn - The callback function to execute for each visible tile.
     *             It receives the tile object, position, and flips as parameters.
     */
    forEachVisibleTile(fn) {
        const { pos, size } = this.scene.getCameraVisibleGrid();
        for (let y = pos.y; y < pos.y + size.y; y++) {
            for (let x = pos.x; x < pos.x + size.x; x++) {
                const tileId = this.getTile(vec2(x, y));
                if (tileId) {
                    const tile = this.scene.getTileObject(tileId);
                    const position = this.scene.getScreenPos(vec2(x, y));
                    const flips = getFlips(tileId);
                    const { H, V } = flips || { H: false, V: false };
                    fn(tile, position, H, V);
                }
            }
        }
    }
    /**
     * Iterates over each visible object in the layer and invokes the provided callback function.
     * The callback function receives the object as a parameter.
     *
     * @param cb - The callback function to be invoked for each visible object.
     */
    forEachVisibleObject(cb) {
        const objects = this.scene.objects.filter(({ layerId }) => layerId === this.id);
        objects.sort((a, b) => a.renderOrder - b.renderOrder);
        for (const obj of objects) {
            obj.visible && cb(obj);
        }
    }
    /**
     * Draws the layer.
     */
    draw() {
        const { camera } = this.scene;
        const { draw } = this.scene.game;
        if (this.visible) {
            switch (this.type) {
                case NodeType.Layer:
                    // draw layer canvas on main canvas
                    draw.copyToMainContext(this.layerCanvas, camera.pos.subtract(vec2(camera.scale)), this.size.multiply(this.scene.tileSize).scale(camera.scale));
                    // render animated tiles on main canvas
                    this.forEachVisibleTile((tile, pos, flipH, flipV) => {
                        tile.animated && tile.draw(pos, flipH, flipV);
                    });
                    break;
                default:
                    this.forEachVisibleObject(obj => obj.draw());
                    break;
            }
        }
    }
}

/**
 * The `Tile` class represents a tile on a tilemap.
 */
class Tile {
    /**
     * Creates a new `Tile` object.
     * @param scene
     * @param id
     * @param tileset
     */
    constructor(scene, id, tileset) {
        this.scene = scene;
        this.id = id;
        this.tileset = tileset;
        /** The size of the tile. */
        this.size = vec2();
        /** Whether the tile is animated. */
        this.animated = false;
        /** The current frame of the animation. */
        this.animFrame = 0;
        /** The start time of the frame. */
        this.then = getPerformance();
        /** The start time of the frame. */
        this.frameStart = getPerformance();
        this.properties = this.getTileProperties(id, this.tileset);
        this.type = (this.properties && this.properties.type) || null;
        this.size = vec2(this.tileset.tilewidth, this.tileset.tileheight);
        this.animated = this.properties && this.properties.animation;
    }
    /**
     * Retrieves the properties of a tile based on its global ID (gid) and the tileset.
     * @param gid - The global ID of the tile.
     * @param tileset - The tileset containing the tile.
     * @returns The properties of the tile, or an empty object if no properties are found.
     */
    getTileProperties(gid, tileset) {
        const { firstgid, tiles } = tileset;
        return (isValidArray(tiles) && tiles.filter(tile => tile.id === gid - firstgid)[0]) || {};
    }
    /**
     * Gets the next GID (Global ID) for the tile.
     * If the tile has an animation, it calculates the next frame based on the animation frames and durations.
     * If the tile does not have an animation, it returns the tile's ID.
     * @returns The next GID for the tile.
     */
    getNextGid() {
        if (this.animated) {
            this.frameStart = getPerformance();
            const { frames } = this.properties.animation;
            if (this.frameStart - this.then > frames[this.animFrame].duration) {
                if (this.animFrame <= frames.length) {
                    this.animFrame = normalize(this.animFrame + 1, 0, frames.length);
                }
                this.then = getPerformance();
            }
            return frames[this.animFrame].tileid + this.tileset.firstgid;
        }
        else
            return this.id;
    }
    /**
     * Retrieves the sprite clip position for the current tile.
     * @returns The sprite clip position as a `vec2` object.
     */
    getSpriteClip() {
        const { columns, firstgid, tilewidth, tileheight } = this.tileset;
        const tileGid = this.getNextGid();
        return vec2(((tileGid - firstgid) % columns) * tilewidth, (Math.ceil((tileGid - firstgid + 1) / columns) - 1) * tileheight);
    }
    /**
     * Draws the tile at the specified position.
     * @param pos - The position to draw the tile at.
     * @param flipH - Whether to flip the tile horizontally (default: false).
     * @param flipV - Whether to flip the tile vertically (default: false).
     * @param angle - The angle to rotate the tile by (default: 0).
     * @param scale - The scale to draw the tile at.
     */
    draw(pos, flipH = false, flipV = false, angle = 0, scale = vec2(this.scene.camera.scale)) {
        const { image, tilewidth, tileheight } = this.tileset;
        const { game, camera } = this.scene;
        game.draw.draw2d(game.getImage(image.source), new Box(pos.add(camera.pos), vec2(tilewidth, tileheight)), scale, angle, flipH, flipV, this.getSpriteClip());
    }
}

class Scene {
    /**
     * Creates a new scene object.
     * @param game - The game object that the scene belongs to.
     * @param name - The name of the scene.
     */
    constructor(game, name) {
        this.game = game;
        this.name = name;
        /** The camera object for the scene. */
        this.camera = new Camera(this);
        /** The size of the scene (grid size in tiles). Default grid is canvas resolution with tile size 1px. */
        this.size = this.game.getResolution();
        /** The size of each tile in the scene (default 1px). */
        this.tileSize = vec2(1);
        /** A record of all the tiles in the scene. */
        this.tiles = {};
        /** An array of tile collision data. */
        this.tileCollisionData = [];
        /** An array of all the objects in the scene. */
        this.objects = [];
        /** An array of all the layers in the scene. */
        this.layers = [];
        /** The global gravity value for the scene. */
        this.gravity = 0;
        /** The next available render order for layers. */
        this.nextRenderOrder = 0;
    }
    /**
     * Destroys the scene by resetting its properties.
     */
    destroy() {
        this.objects = [];
        this.layers = [];
        this.tiles = {};
        this.tileCollisionData = [];
        this.nextRenderOrder = 0;
        this.camera.pos = vec2();
    }
    /**
     * Performs pre-initialization tasks for the scene.
     * If a TMX map is provided, it sets the dimensions, creates tilesets, and creates layers based on the map.
     */
    preInit() {
        return __awaiter(this, void 0, void 0, function* () {
            if (this.tmxMap && typeof this.tmxMap === 'string') {
                this.tmxMap = yield tmx(this.tmxMap);
            }
        });
    }
    createFromTmxMap() {
        if (typeof this.tmxMap === 'object') {
            const { layers, tilesets, tilewidth, tileheight, width, height } = this.tmxMap;
            this.setDimensions(vec2(width, height), vec2(tilewidth, tileheight));
            this.createTilesets(tilesets);
            this.createLayers(layers);
        }
    }
    /**
     * Initializes the scene.
     */
    init() { }
    /**
     * Updates the scene.
     */
    update() { }
    /**
     * Updates the camera in the scene.
     */
    updateCamera() {
        this.camera.update();
    }
    /**
     * Updates all the layers in the scene.
     */
    updateLayers() {
        for (const layer of this.layers)
            layer instanceof Layer && layer.update();
    }
    /**
     * Updates all the active objects in the scene.
     */
    updateObjects() {
        for (const obj of this.objects) {
            if (obj.active) {
                obj.update();
                obj.dead && this.removeObject(obj);
            }
        }
    }
    /**
     * Performs post-update operations for the scene.
     */
    postUpdate() { }
    /**
     * Performs post-update operations for all the layers in the scene.
     */
    postUpdateLayers() {
        for (const layer of this.layers)
            layer instanceof Layer && layer.postUpdate();
    }
    /**
     * Draws the scene on the main context.
     */
    draw() {
        const { ctx, width, height } = this.game;
        const layers = this.layers.filter(l => l instanceof Layer && l.visible);
        const objects = this.objects.filter(o => o.visible && !o.layerId);
        layers.sort(sortByRenderOrder);
        objects.sort(sortByRenderOrder);
        // ctx.save()
        ctx.imageSmoothingEnabled = false; // always pixel perfect
        ctx.clearRect(0, 0, width, height);
        for (const layer of layers)
            layer.draw();
        for (const obj of objects)
            obj.draw();
        // ctx.restore()
        this.game.debug && this.displayDebug();
    }
    /**
     * Sets the dimensions of the scene.
     * @param size - The size of the scene.
     * @param tileSize - The size of each tile in the scene.
     */
    setDimensions(size, tileSize) {
        this.size = size;
        this.tileSize = tileSize;
    }
    /**
     * Sets the scale of the scene.
     * @param scale - The scale value to set.
     */
    setScale(scale) {
        this.camera.setScale(scale);
    }
    /**
     * Sets the tile collision layer for the scene.
     * @param layerIndex - The index of the layer to set as the collision layer.
     */
    setTileCollisionLayer(layerIndex, exclude = []) {
        var _a;
        const layer = this.layers[layerIndex];
        if (layer) {
            this.tileCollisionData =
                ((_a = layer === null || layer === void 0 ? void 0 : layer.data) === null || _a === void 0 ? void 0 : _a.map((id) => {
                    return id && !exclude.includes(id) ? id : 0;
                })) || [];
        }
    }
    /**
     * Performs a tile collision test for the given position and size.
     * @param pos - The position of the entity.
     * @param size - The size of the entity.
     * @param entity - The entity to perform the collision test for (optional).
     * @returns `true` if a collision is detected, `false` otherwise.
     */
    testTileCollision(pos, size, entity) {
        const minX = Math.max((pos.x - size.x / 2) | 0, 0);
        const minY = Math.max((pos.y - size.y / 2) | 0, 0);
        const maxX = Math.min(pos.x + size.x / 2, this.size.x);
        const maxY = Math.min(pos.y + size.y / 2, this.size.y);
        for (let y = minY; y < maxY; ++y) {
            for (let x = minX; x < maxX; ++x) {
                const tileData = this.tileCollisionData[x + this.size.x * y];
                if (tileData && (!entity || entity.collideWithTile(tileData, vec2(x, y))))
                    return true;
            }
        }
        return false;
    }
    /**
     * Performs a raycast to check for tile collisions between two positions.
     * Based on the DDA algorithm: https://lodev.org/cgtutor/raycasting.html
     * @param start The starting position of the raycast.
     * @param end The ending position of the raycast.
     * @param entity An optional entity to check for collisions with tiles.
     * @returns The position of the first tile hit by the raycast, or null if no collision occurred.
     */
    raycastTileCollision(start, end, entity) {
        const pos = start.floor();
        const delta = end.subtract(start);
        const length = delta.length();
        const norm = delta.normalize();
        const unit = vec2(Math.abs(1 / norm.x), Math.abs(1 / norm.y));
        let x1 = unit.x * (delta.x < 0 ? start.x - pos.x : pos.x - start.x + 1);
        let y1 = unit.y * (delta.y < 0 ? start.y - pos.y : pos.y - start.y + 1);
        while (1) {
            const tileId = this.getTileCollisionData(pos);
            if (tileId && (!entity || entity.collideWithTileRaycast(tileId, pos))) {
                return pos.add(vec2(0.5));
            }
            if (x1 > length && y1 > length)
                break;
            if (x1 > y1)
                (pos.y += Math.sign(delta.y)), (y1 += unit.y);
            else
                (pos.x += Math.sign(delta.x)), (x1 += unit.x);
        }
    }
    /**
     * Creates layers and adds them to the scene.
     * @param layers - An array of layer constructors or TMXLayer instances.
     */
    createLayers(layers) {
        this.layers = [];
        layers.forEach(l => this.addLayer(l));
    }
    /**
     * Adds a layer to the scene.
     * @param l - The layer to add. It can be a constructor function or an instance of TMXLayer.
     * @param order - The render order of the layer. If not provided,
     *                it will be assigned the next available render order.
     */
    addLayer(l, order) {
        const layer = typeof l === 'function' ? new l(this) : new Layer(this, l);
        // Extract object from TMXLayer
        if (typeof l !== 'function') {
            l.objects && l.objects.forEach(obj => this.addObjectFromLayer(obj.type, Object.assign(Object.assign({}, obj), { layerId: l.id })));
        }
        layer.renderOrder = order || this.nextRenderOrder++;
        this.layers.push(layer);
        return layer;
    }
    /**
     * Adds an object to the scene from a specific layer.
     *
     * @param type - The type of the object to add.
     * @param props - The properties of the object.
     * @returns The added entity.
     */
    addObjectFromLayer(type, props) {
        const Model = this.game.objectClasses[type];
        const entity = Model ? new Model(this, props) : new Entity(this, props);
        this.objects.push(entity);
        return entity;
    }
    /**
     * Adds an entity to the scene.
     * @param entity - The entity to be added.
     * @param layerId - The ID of the layer to add the entity to (optional).
     * @returns The added entity.
     */
    addObject(entity, layerId) {
        if (layerId) {
            const layer = this.getLayerById(layerId);
            layer instanceof Layer
                ? (entity.layerId = layer.id)
                : console.warn(`Layer with ID:${layerId} not found or is not an object layer!`);
        }
        this.objects.push(entity);
        return entity;
    }
    /**
     * Removes an object from the scene.
     * @param obj - The object to be removed.
     */
    removeObject(obj) {
        this.objects.splice(this.objects.indexOf(obj), 1);
    }
    /**
     * Adds a tileset to the scene.
     * @param tileset - The tileset to add.
     * @param source - The source of the tileset image.
     */
    addTileset(tileset, source) {
        const newTileset = Object.assign(Object.assign({}, tileset), { image: Object.assign(Object.assign({}, tileset.image), { source }) });
        for (let i = 0; i < newTileset.tilecount; i++) {
            this.tiles[i + newTileset.firstgid] = new Tile(this, i + newTileset.firstgid, newTileset);
        }
        return newTileset;
    }
    /**
     * Creates tilesets for the scene.
     * @param tilesets - An array of TMXTileset objects representing the tilesets to be created.
     */
    createTilesets(tilesets) {
        tilesets.map((tileset) => {
            const asset = getFilename(tileset.image.source);
            if (Object.keys(this.game.images).includes(asset)) {
                this.addTileset(tileset, asset);
            }
        });
    }
    /**
     * Sets the collision data for a tile at the specified position.
     * @param pos - The position of the tile.
     * @param data - The collision data to set for the tile. Defaults to 0.
     */
    setTileCollisionData(pos, data = 0) {
        pos.inRange(this.size) && (this.tileCollisionData[((pos.y | 0) * this.size.x + pos.x) | 0] = data);
    }
    /**
     * Retrieves the tile collision data at the specified position.
     * @param pos - The position to retrieve the tile collision data from.
     * @returns The tile collision data at the specified position.
     */
    getTileCollisionData(pos) {
        return pos.inRange(this.size) ? this.tileCollisionData[((pos.y | 0) * this.size.x + pos.x) | 0] : 0;
    }
    /**
     * Returns the visible grid of the camera.
     * @returns {Box} The visible grid of the camera.
     */
    getCameraVisibleGrid() {
        return new Box(this.camera.pos.divide(this.tileSize).invert().divide(this.camera.scale).floor(), this.game.getResolution().divide(this.tileSize).divide(this.camera.scale).add(vec2(1)));
    }
    /**
     * Calculates the visible area of the camera in the scene.
     * @returns A `Box` object representing the visible area of the camera.
     */
    getCameraVisibleArea() {
        const viewSize = this.game.getResolution();
        return new Box(this.camera.pos.invert().add(viewSize.divide(2)).divide(this.camera.scale), viewSize.divide(this.camera.scale)).divide(this.tileSize);
    }
    /**
     * Returns the grid position of a given vector.
     * @param pos - The vector to calculate the grid position for.
     * @returns The grid position as a vector.
     */
    getGridPos(pos) {
        return vec2(Math.ceil(pos.x / this.tileSize.x), Math.ceil(pos.y / this.tileSize.y));
    }
    /**
     * Calculates position on screen based on the given position and size.
     * @param pos - The position vector.
     * @param size - The size vector (optional).
     * @returns The calculated screen position.
     */
    getScreenPos(pos, size) {
        return pos
            .clone()
            .multiply(this.tileSize)
            .subtract(size ? size.multiply(this.tileSize).divide(2) : vec2())
            .scale(this.camera.scale)
            .subtract(vec2(this.camera.scale)); // @todo: check if this is needed
    }
    /**
     * Returns the position of the pointer relative to the scene.
     * @returns {Vector2} The position of the pointer relative to the scene.
     */
    getPointerPos() {
        const { pos, scale } = this.camera;
        const { mouseScreenPos } = this.game.input;
        return mouseScreenPos.clone().subtract(pos).divide(scale);
    }
    /**
     * Returns the pointer position relative to the grid.
     * @returns The pointer position divided by the tile size.
     */
    getPointerRelativeGridPos() {
        return this.getPointerPos().divide(this.tileSize);
    }
    /**
     * Retrieves a layer from the scene based on the provided ID.
     * @param id - The ID of the layer to retrieve.
     * @returns The layer with the specified ID, or an empty object if no layer is found.
     */
    getLayerById(id) {
        return this.layers.find(layer => layer.id === id) || {};
    }
    /**
     * Retrieves the layer at the specified index.
     * If the layer does not exist, an empty `Layer` object is returned.
     * @param index - The index of the layer to retrieve.
     * @returns The layer at the specified index, or an empty `Layer` object if the layer does not exist.
     */
    getLayerByIndex(index) {
        return this.layers[index] || {};
    }
    /**
     * Retrieves an object from the scene by its ID.
     * @param id - The ID of the object to retrieve.
     * @returns The object with the specified ID, or `undefined` if not found.
     */
    getObjectById(id) {
        return this.objects.find(object => object.id === id);
    }
    /**
     * Retrieves an object from the scene by its type.
     * @param type - The type of the object to retrieve.
     * @returns The first object found with the specified type, or undefined if no object is found.
     */
    getObjectByType(type) {
        return this.objects.find(object => object.type === type);
    }
    /**
     * Returns an array of objects of a specific type.
     * @param type - The type of objects to filter.
     * @returns An array of objects of the specified type.
     */
    getObjectsByType(type) {
        return this.objects.filter(object => object.type === type);
    }
    /**
     * Retrieves the object layers of the scene.
     * @returns An array of object layers.
     */
    getObjectLayers() {
        return this.layers.filter((layer) => isValidArray(layer.objects));
    }
    /**
     * Retrieves the tile object at the specified position on the given layer.
     * If the tile at the position is not found, it returns the tile object at index 0.
     * @param pos - The position of the tile.
     * @param layerId - The ID of the layer.
     * @returns The tile object at the specified position.
     */
    getTile(pos, layerId) {
        return this.getTileObject(this.getLayerById(layerId).getTile(pos) || 0);
    }
    /**
     * Retrieves the tile object with the specified ID.
     * @param id - The ID of the tile object.
     * @returns The tile object associated with the ID.
     */
    getTileObject(id) {
        const gid = (id &= ~(Flipped.Horizontally | Flipped.Vertically | Flipped.Diagonally));
        return this.tiles[gid];
    }
    /**
     * Removes a layer from the scene at the specified index.
     * @param index - The index of the layer to remove.
     */
    removeLayer(index) {
        this.layers.splice(index, 1);
    }
    /**
     * Displays debug information on the canvas.
     */
    displayDebug() {
        const { avgFPS, canvas, draw, primaryColor } = this.game;
        const { pos, scale } = this.camera;
        const { width, height } = canvas;
        const grid = this.getCameraVisibleGrid();
        const pointerPos = this.getPointerPos();
        const write = (text, align = 'left') => {
            const x = (align === 'left' && 4) || (align === 'right' && width - 4) || width / 2;
            draw.text(text, vec2(x, height - 4), primaryColor, '1em', align, 'bottom', true);
        };
        write(`[${grid.pos.x},${grid.pos.y}][${pos.x.toFixed(2)},${pos.y.toFixed(2)}][x${scale.toFixed(1)}]`);
        write(`[${pointerPos.x.toFixed(2)},${pointerPos.y.toFixed(2)}]`, 'center');
        write(`[${this.objects.length}][${avgFPS.toFixed(1)} FPS]`, 'right');
    }
}

/**
 * Represents a game engine that manages scenes, assets, and game state.
 */
class Game {
    /**
     * Creates a new game instance.
     * @param {GameConfig} config - The game configuration.
     * @param {Record<string, string>} preload - The preload record.
     */
    constructor(config, preload) {
        this.config = config;
        this.preload = preload;
        /** Draw object for rendering shapes and images. */
        this.draw = new Draw(this);
        /** Input object for handling user input. */
        this.input = new Input(this);
        /** Flag indicating whether the game should render pixel-perfect. */
        this.pixelPerfect = false;
        /** Background color of the game. */
        this.backgroundColor = DefaultColors.DarkBlue;
        /** Primary color. Used for text, and other UI elements like preloader. */
        this.primaryColor = DefaultColors.White;
        /** Secondary color. Used for text, and other UI elements like preloader. */
        this.secondaryColor = DefaultColors.LightBlue;
        /** Image assets record containing image elements. */
        this.images = {};
        /** Sound assets record containing Howl objects. */
        this.sounds = {};
        /** Object classes record containing entity classes. */
        this.objectClasses = {};
        /** Scene classes record containing scene classes. */
        this.sceneClasses = {};
        /** Current scene being played. */
        this.currentScene = null;
        /** Scene record containing all scenes. */
        this.scenes = {};
        /** Optional settings for the game. */
        this.settings = {};
        /** Game width. */
        this.width = window.innerWidth;
        /** Game height. */
        this.height = window.innerHeight;
        /** Debug mode flag. */
        this.debug = false;
        /** Game pause flag. */
        this.paused = false;
        /** Game frame rate. */
        this.frameRate = 60;
        /** Game delta time. */
        this.delta = 1 / 60;
        /** Game average frames per second. */
        this.avgFPS = 0;
        /** Game frame counter. */
        this.frame = 0;
        /** Game time. */
        this.time = 0;
        /** Game time in real seconds. */
        this.timeReal = 0;
        /** Game frame time last. */
        this.frameTimeLast = 0;
        /** Game frame time buffer. */
        this.frameTimeBuffer = 0;
        /** Game preloader percent. */
        this.preloadPercent = 0;
        /** Game global audio volume. */
        this.audioVolume = 1;
        this.canvas = document.createElement('canvas');
        this.objectClasses = (config === null || config === void 0 ? void 0 : config.entities) || {};
        this.sceneClasses = (config === null || config === void 0 ? void 0 : config.scenes) || {};
        this.debug = !!config.debug;
        this.pixelPerfect = !!(config === null || config === void 0 ? void 0 : config.pixelPerfect);
        this.backgroundColor = (config === null || config === void 0 ? void 0 : config.backgroundColor) ? new Color(config.backgroundColor) : this.backgroundColor;
        this.primaryColor = (config === null || config === void 0 ? void 0 : config.primaryColor) ? new Color(config.primaryColor) : this.primaryColor;
        this.secondaryColor = (config === null || config === void 0 ? void 0 : config.secondaryColor) ? new Color(config.secondaryColor) : this.secondaryColor;
        this.canvas.setAttribute('style', `${CanvasStyle} ${this.pixelPerfect ? 'image-rendering: pixelated;' : ''}`);
        this.canvas.style.backgroundColor = this.backgroundColor.toString();
        this.ctx = this.canvas.getContext('2d');
        window.addEventListener('resize', this.onResize.bind(this));
        this.onResize();
        document.body.style.cssText = `${BodyStyle} background: ${this.backgroundColor.toString()};`;
        document.body.appendChild(this.canvas);
        if (!!config.global)
            window.Platfuse = this;
    }
    /**
     * Starts the game engine and initializes the scenes.
     * @param sceneName - Optional name of the scene to start with.
     */
    start(sceneName) {
        return __awaiter(this, void 0, void 0, function* () {
            this.update();
            const assets = yield preload(this.preload, p => (this.preloadPercent = p));
            Object.keys(assets).forEach((key) => {
                if (assets[key] instanceof HTMLImageElement) {
                    this.images[key] = assets[key];
                }
                else if (assets[key] instanceof Howl) {
                    this.sounds[key] = assets[key];
                }
            });
            yield Promise.all(Object.keys(this.sceneClasses).map((sceneName) => __awaiter(this, void 0, void 0, function* () {
                const Model = this.sceneClasses[sceneName];
                const s = new Model(this, sceneName);
                yield s.preInit();
                this.scenes[sceneName] = s;
            })));
            yield delay(1000);
            if (sceneName) {
                this.playScene(sceneName);
            }
        });
    }
    /**
     * Plays the specified scene.
     * @param sceneName - The name of the scene to play.
     * @param restart - Whether to restart the scene if it is already playing. Default is `false`.
     * @throws Error if the specified scene is not found.
     */
    playScene(sceneName, restart = false) {
        if (this.currentScene && this.currentScene.name === sceneName && !restart)
            return;
        if (!this.scenes[sceneName]) {
            throw new Error(`Scene '${sceneName}' not found!`);
        }
        if (this.currentScene) {
            this.currentScene.destroy();
        }
        const scene = this.scenes[sceneName];
        if (scene.tmxMap) {
            scene.createFromTmxMap();
        }
        scene.init();
        this.currentScene = scene;
    }
    /**
     * Restarts the current scene.
     */
    restartScene() {
        if (this.currentScene) {
            this.playScene(this.currentScene.name, true);
        }
    }
    /**
     * Handles the resize event of the game window.
     */
    onResize() {
        var _a;
        this.width = window.innerWidth;
        this.height = window.innerHeight;
        if (((_a = this.config) === null || _a === void 0 ? void 0 : _a.fixedSize) instanceof Vector) {
            const { fixedSize } = this.config;
            const aspect = innerWidth / innerHeight;
            const fixedAspect = fixedSize.x / fixedSize.y;
            this.canvas.width = fixedSize.x;
            this.canvas.height = fixedSize.y;
            this.canvas.style.width = aspect < fixedAspect ? '100%' : '';
            this.canvas.style.height = aspect < fixedAspect ? '' : '100%';
        }
        else {
            this.canvas.width = this.width;
            this.canvas.height = this.height;
        }
        this.width = this.canvas.width;
        this.height = this.canvas.height;
    }
    /**
     * Updates the game state and renders the scene.
     * @param frameTime The time elapsed since the last frame in milliseconds.
     */
    update(frameTime = 0) {
        const scene = this.currentScene;
        const frameTimeDelta = frameTime - this.frameTimeLast;
        this.frameTimeLast = frameTime;
        this.avgFPS = lerp(0.05, this.avgFPS, 1e3 / (frameTimeDelta || 1));
        this.timeReal += frameTimeDelta / 1e3;
        this.frameTimeBuffer += (this.paused ? 0 : 1) * frameTimeDelta;
        if (scene instanceof Scene) {
            if (this.paused) {
                scene.postUpdate();
                this.input.postUpdate();
            }
            else {
                let deltaSmooth = 0;
                if (this.frameTimeBuffer < 0 && this.frameTimeBuffer > -9) {
                    deltaSmooth = this.frameTimeBuffer;
                    this.frameTimeBuffer = 0;
                }
                for (; this.frameTimeBuffer >= 0; this.frameTimeBuffer -= 1e3 / this.frameRate) {
                    this.time = this.frame++ / this.frameRate;
                    this.delta = 1 / this.avgFPS;
                    // update input
                    this.input.update();
                    // update scene
                    scene.updateLayers();
                    scene.updateObjects();
                    scene.update();
                    scene.updateCamera();
                    // do post update
                    scene.postUpdate();
                    scene.postUpdateLayers();
                    this.input.postUpdate();
                }
                this.frameTimeBuffer += deltaSmooth;
            }
            scene.draw();
        }
        else {
            this.draw.preloader(this.preloadPercent);
        }
        this.animationFrame = requestAnimationFrame((time) => this.update(time));
    }
    /**
     * Toggles the pause state of the game.
     * @param paused - Optional. Specifies whether the game should be paused or not. Default is true.
     */
    togglePause(paused = !this.paused) {
        this.paused = paused;
    }
    /**
     * Gets the resolution of the game.
     * @returns A Vector representing the width and height of the game.
     */
    getResolution() {
        return new Vector(this.width, this.height);
    }
    /**
     * Sets the optional settings for the game.
     * @param value - The new settings value.
     */
    setSettings(value) {
        this.settings = value;
    }
    /**
     * Sets a setting value for the game.
     * @param key - The key of the setting.
     * @param value - The value to set for the setting.
     */
    setSetting(key, value) {
        this.settings = Object.assign(Object.assign({}, this.settings), { [key]: value });
    }
    /**
     * Retrieves the value of a setting based on the provided key.
     * @param key - The key of the setting to retrieve.
     * @returns The value of the setting.
     */
    getSetting(key) {
        return this.settings[key];
    }
    /**
     * Creates a new Timer object.
     * @param timeLeft - The initial time left for the timer (optional).
     * @returns A new Timer instance.
     */
    timer(timeLeft) {
        return new Timer(this, timeLeft);
    }
    /**
     * Retrieves an image by its name from the assets.
     * @param name - The name of the image to retrieve.
     * @returns The HTMLImageElement corresponding to the specified name.
     * @throws Error if the image is not found in the assets.
     */
    getImage(name) {
        if (this.images[name])
            return this.images[name];
        else
            throw new Error(`'${name}' is not a valid image!`);
    }
    /**
     * Plays the sound with the specified name.
     * @param name - The name of the sound to play.
     */
    playSound(name) {
        if (this.sounds[name]) {
            this.sounds[name].volume(this.audioVolume);
            this.sounds[name].play();
        }
    }
    /**
     * Sets the global audio volume.
     * @param volume - The volume level to set.
     */
    setAudioVolume(volume) {
        this.audioVolume = volume;
    }
}

export { Box, Camera, Color, DefaultColors, Draw, Emitter, Entity, Flipped, Game, Input, Layer, NodeType, Particle, Scene, Shape, Sprite, Tile, Timer, Vector, box, clamp, deg2rad, delay, lerp, normalize, percent, rad2deg, rand, randInt, randVector, vec2, wait };
